import React, { useState, useEffect, useRef, useMemo, useCallback } from "react";
import Header from "../component/Header";
import Sidebar from "../component/Sidebar";
import "../css/sidebar.css";
import "../css/modern-modal.css";
import "../css/setTargetModal.css";
import { NavLink } from "react-router-dom";
import { Tooltip, Whisper } from "rsuite";
import "rsuite/dist/rsuite.min.css";
import { Placeholder } from "rsuite";
import LogoApple from "../images/company-logos/apple.svg";
import LogoIgne from "../images/company-logos/igne.svg";
import LogoTesla from "../images/company-logos/tesla.svg";
import LogoJNJ from "../images/company-logos/jnj.svg";
import { SelectPicker, Message } from "rsuite";
import { motion, AnimatePresence } from "framer-motion";
import ArrowTrendUp from "../images/arrow-trend-up.svg";
import ArrowTrendDown from "../images/arrow-trend-down.svg";
import { toast } from "react-toastify";
import { MdKeyboardDoubleArrowLeft } from "react-icons/md";
import { MdKeyboardDoubleArrowRight } from "react-icons/md";
import { IoIosArrowBack } from "react-icons/io";
import { IoIosArrowForward } from "react-icons/io";
import { useNavigate } from "react-router-dom";
import MarketDepthIcon from "../component/MarketDepthIcon";
import TablePageSizeSelect from "../component/TablePageSizeSelect";
import { TbTarget } from "react-icons/tb";
import SetTargetModal from "../component/SetTargetModal";
import { Modal } from "bootstrap";
import { EllipsisVertical, ArrowUp, ArrowDown } from "lucide-react";
import { createPortal } from "react-dom";
import { getCurrentLanguage, t, translateColumnLabel } from "../i18n";
import fivePaisaWebSocketService from "../services/fivePaisaWebSocketService";
import fivePaisaMarketDepthStore from "../services/fivePaisaMarketDepthStore";
import angelOneWebSocketService, {
  getAngelExchangeTypeFromHolding,
} from "../services/angelOneWebSocketService";
import growwLivePriceService from "../services/growwLivePriceService";
import angelOneMarketDepthService, {
  normalizeAngelMarketDepth,
} from "../services/angelOneMarketDepthService";


import {
  placeOrderViaPms,
  normalizePlaceOrderResponse,
  isFivePaisaBroker,
  runPlaceOrderAcceptedFlow,
} from "../services/placeOrderService";

import StockCandleModal from "../component/StockCandleModal";
import MarketDepthPopover from "../component/MarketDepthPopover";

const GROW_POLL_SOURCE = "position";

const CANDLE_CHART_BROKERS = new Set(["5paisa", "angelone"]);

const supportsCandleChart = (brokerType) =>
  CANDLE_CHART_BROKERS.has(String(brokerType || "").trim().toLowerCase());

const toPmsBrokerFlag = (brokerType) => {
  const normalized = String(brokerType || "").trim().toLowerCase();
  if (normalized === "grow" || normalized === "5paisa" || normalized === "angelone") {
    return normalized;
  }
  return "5paisa";
};

function Position(props) {
  return <PositionContent {...props} />;
}

const POSITION_PMS_COLUMN_KEY_TO_TABLE_COL = {
  name: "Name",
  pc_ab: "Previous Close",
  net_buy_quantity: "Net Quantity",
  current_buy_value: "Current Value",
  today_high_low: "TodaysHigh",
  current_price: "Current Price",
  net_quantity: "Net Quantity",
  net_qty: "Net Quantity",
  current_value: "Current Value",
  previous_close: "Previous Close",
  todays_high: "TodaysHigh",
  todays_low: "TodaysLow",
  buy_value: "Buy Value",
  buy_quantity: "Buy Quantity",
  buy_qty: "Buy Quantity",
  avg_buy_price: "Avg Buy Price",
  avg_buy_rate: "Avg Buy Price",
  sell_quantity: "Sell Quantity",
  sell_qty: "Sell Quantity",
  sell_value: "Sell Value",
  avg_sell_price: "Avg Sell Price",
  profit_loss: "Profit/Loss",
  pl: "Profit/Loss",
  target_price: "Target Price",
  target_percent: "Target %",
  target_pct: "Target %",
};

const POSITION_TABLE_COL_LABEL_ALIASES = {
  "PC/AB": "Previous Close",
  "Net/Buy Quantity": "Net Quantity",
  "Current/Buy Value": "Current Value",
  "Today High/Low": "TodaysHigh",
  "Profit/Loss": "Profit/Loss",
};

const POSITION_TABLE_COL_TO_PMS_COLUMN_KEY = {
  ...Object.fromEntries(
    Object.entries(POSITION_PMS_COLUMN_KEY_TO_TABLE_COL).map(
      ([apiKey, tableCol]) => [tableCol, apiKey],
    ),
  ),
  "Previous Close": "pc_ab",
  "Net Quantity": "net_buy_quantity",
  "Current Value": "current_buy_value",
  TodaysHigh: "today_high_low",
};

function mapPositionPmsColumnToTableCol(columnKey, columnLabel) {
  const key = String(columnKey || "").toLowerCase().trim();
  if (POSITION_PMS_COLUMN_KEY_TO_TABLE_COL[key]) {
    return POSITION_PMS_COLUMN_KEY_TO_TABLE_COL[key];
  }
  const label = String(columnLabel || "").trim();
  if (POSITION_TABLE_COL_LABEL_ALIASES[label]) {
    return POSITION_TABLE_COL_LABEL_ALIASES[label];
  }
  const known = new Set([
    "Name",
    "Net Quantity",
    "Current Price",
    "Current Value",
    "TodaysHigh",
    "TodaysLow",
    "Previous Close",
    "Buy Value",
    "Buy Quantity",
    "Avg Buy Price",
    "Sell Value",
    "Sell Quantity",
    "Avg Sell Price",
    "Profit/Loss",
    "Target Price",
    "Target %",
    "Actions",
  ]);
  if (known.has(label)) return label;
  return null;
}

function buildPositionTableColumnOrderFromPms(columnsPayload) {
  if (!Array.isArray(columnsPayload) || columnsPayload.length === 0) return null;
  const sorted = [...columnsPayload].sort(
    (a, b) => Number(a.sort_order) - Number(b.sort_order),
  );
  const out = [];
  const seen = new Set();
  for (const c of sorted) {
    const selected =
      c.is_selected === "1" || c.is_selected === 1 || c.is_selected === true;
    const active =
      c.is_active === "1" || c.is_active === 1 || c.is_active === true;
    if (!selected || !active) continue;
    const col = mapPositionPmsColumnToTableCol(c.column_key, c.column_label);
    if (!col || col === "Actions") continue;
    if (seen.has(col)) continue;
    seen.add(col);
    out.push(col);
  }
  if (!out.includes("Actions")) out.push("Actions");
  return out.length ? out : null;
}

function PositionContent(props) {
  // All initialization and Declaration
  const [tabName, setTabName] = useState("Position");
  const [progress, setProgress] = useState(0);
  const [holdings, setHoldings] = useState([]);

  // WebSocket related state variables
  const [, setConnected] = useState(false);
  const [liveUpdateTrigger, setLiveUpdateTrigger] = useState(0);
  const [isLoading, setIsLoading] = useState(true);
  // Market depth state (stores the latest order book for subscribed stock)
  const [marketDepth, setMarketDepth] = useState([]);
  const [currentMDModal, setcurrentMDModal] = useState(null);
  const searchInputRef = useRef(null);

  // Inside your component
  const navigate = useNavigate();
  const [currentLanguage, setCurrentLanguage] = useState(getCurrentLanguage());
  const tr = (key) => t(key, currentLanguage);










  const sharedData = JSON.parse(localStorage.getItem("sharedData") || "null");
  const pmsApiBaseUrl =
    process.env.REACT_APP_PMS_API_BASE_URL || "https://pmsapi.infomanav.in";
  const activeBrokerFlag = toPmsBrokerFlag(
    sharedData?.broker_type || localStorage.getItem("broker_type") || "5paisa",
  );
  const isAngelOneBroker = activeBrokerFlag === "angelone";

  // Refs for caching live data and WebSocket listeners
  const wsStatusUnsubRef = useRef(null);
  const wsPriceUnsubRef = useRef(null);
  const wsErrorUnsubRef = useRef(null);
  const mdStoreUnsubRef = useRef(null);
  const angelWsPriceUnsubRef = useRef(null);
  const angelWsErrorUnsubRef = useRef(null);
  const angelPositionSubscriptionsRef = useRef([]);
  const angelMdServiceUnsubRef = useRef(null);

  const [showMarketDepth, setShowMarketDepth] = useState(false);
  const [marketDepthPosition, setMarketDepthPosition] = useState({
    top: 0,
    left: 0,
  });
  const marketDepthButtonRef = useRef(null);
  const marketDepthPopoverRef = useRef(null);

  // Include click-outside useEffect + handleMarketDepthClick function

  const livePriceCache = useRef({});
  const liveHighCache = useRef({});
  const liveLowCache = useRef({});
  const livePCloseCache = useRef({});
  const liveDiffCache = useRef({});
  const liveChangePercentageCache = useRef({});
  const liveVolumeCache = useRef({});
  const livePLCache = useRef({});
  const currentMDModalRef = useRef(null);
  const positionFetchIdRef = useRef(0);
  const lastPositionFetchRunRef = useRef({ key: "", at: 0 });

  const OrderType = [
    { value: "Market Order", label: tr("marketOrder") },
    { value: "Limit Order", label: tr("limitOrder") },
  ];

  const [orderType, setOrderType] = useState("Market Order");
  useEffect(() => {
    const handleLanguageChange = (event) => {
      setCurrentLanguage(event?.detail?.language || getCurrentLanguage());
    };
    window.addEventListener("app-language-change", handleLanguageChange);
    return () =>
      window.removeEventListener("app-language-change", handleLanguageChange);
  }, []);
  // const [qty, setQty] = useState(1);
  const [isPreview, setIsPreview] = useState(false);

  const [columns, setColumns] = useState([
    "Name",
    "Net Quantity",
    "Current Price",
    "Current Value",
    "TodaysHigh",
    "TodaysLow",
    "Previous Close",
    "Buy Value",
    "Buy Quantity",
    "Avg Buy Price",
    // "Sell Value",
    // "Sell Quantity",
    // "Avg Sell Price",
    "Profit/Loss",
    "Target Price",
    "Target %",
    "Actions",
  ]);

  // const [columns, setColumns] = useState([
  //     { key: "Name", label: "Name" },
  //     { key: "NetQty", label: "Net Quantity" },
  //     { key: "LTP", label: "Current Price" },
  //     { key: "PreviousClose", label: "Previous Close" },
  //     { key: "BuyAvg", label: "Avg Buy Price" },
  //     { key: "BuyValue", label: "Buy Value" },
  //     { key: "PnL", label: "Profit/Loss" },
  //     { key: "BuyQty", label: "Buy Quantity" },
  //     { key: "SellQty", label: "Sell Quantity" },
  //     { key: "SellAvg", label: "Avg Sell Price" },
  //     { key: "SellValue", label: "Sell Value" },
  //     { key: "TargetPrice", label: "Target Price" },
  //     { key: "TargetPct", label: "Target %" },
  //     { key: "Actions", label: "" },
  //   ]);

  const [buySellModalDetails, setBuySellModalDetails] = useState({
    FullName: "",
    Symbol: "",
    Type: "Buy", // hardcoded for buy function, will create separate for sell
    OrderQty: 1, // default 1
    LimitPrice: 0, // default 0
    order: null, // complete order object for future use
  });
  const [growwTpinErrorText, setGrowwTpinErrorText] = useState("");

  const formatNumber = (num) => {
    if (num === null || num === undefined) return "-";
    const number = parseFloat(num);
    if (isNaN(number)) return "-";

    return number.toLocaleString("en-IN", {
      minimumFractionDigits: 2,
      maximumFractionDigits: 2,
    });
  };

  // Drag-N-Drop
  // Add these in your component
  const [draggedColumn, setDraggedColumn] = useState(null);
  const [columnOrder, setColumnOrder] = useState(columns); // start with columns prop or state
  const [updatePositionTrigger, setUpdatePositionTrigger] = useState(0);

  const [targetDetails, setTargetDetails] = useState();
  const [targetReasonModalData, setTargetReasonModalData] = useState({
    stockName: "",
    reason: "",
    details: "",
  });

  // table search
  const [searchQuery, setSearchQuery] = useState("");

  // Pagination
  const [currentPage, setCurrentPage] = useState(1);
  const [pageSize, setPageSize] = useState(10);
  const [activeHeaderMenu, setActiveHeaderMenu] = useState(null);
  const [sortConfig, setSortConfig] = useState(null);

  const [selectedStock, setSelectedStock] = useState(null);

  const handleRowClick = (holding) => {
    setSelectedStock(holding);
  };

  const broker_type = String(
    localStorage.getItem("broker_type") || sharedData?.broker_type || "",
  ).toLowerCase();


  //----------------------------// useEffects //----------------------------//

  useEffect(() => {
    const userId = Number(localStorage.getItem("id")) || Number(sharedData?.ID) || 0;
    if (!userId || !activeBrokerFlag) {
      setIsLoading(false);
      setHoldings([]);
      return;
    }
    // React StrictMode in dev runs effects twice on mount — skip duplicate within 1.2s
    const runKey = `${userId}:${activeBrokerFlag}:${updatePositionTrigger}`;
    const now = Date.now();
    if (
      lastPositionFetchRunRef.current.key === runKey &&
      now - lastPositionFetchRunRef.current.at < 1200
    ) {
      return;
    }
    lastPositionFetchRunRef.current = { key: runKey, at: now };
    fetchPositions(
      sharedData.accessToken,
      sharedData.ClientCode,
      sharedData.api_key,
    );
  }, [updatePositionTrigger, activeBrokerFlag]);

  useEffect(() => {
    if (activeBrokerFlag !== "5paisa") {
      return undefined;
    }

    wsStatusUnsubRef.current = fivePaisaWebSocketService.onStatus((status) => {
      setConnected(status?.type === "connected");
    });

    wsPriceUnsubRef.current = fivePaisaWebSocketService.onPrice((tick) => {
      if (tick) handleLivePriceUpdate(tick);
    });

    mdStoreUnsubRef.current = fivePaisaMarketDepthStore.subscribe((snap) => {
      const details = Array.isArray(snap?.activeDepthData)
        ? snap.activeDepthData
        : [];
      setMarketDepth(details);
    });

    wsErrorUnsubRef.current = fivePaisaWebSocketService.onError((error) => {
      console.error("❌ WS service error:", error);
    });

    fivePaisaWebSocketService.connect();

    return () => {
      if (wsStatusUnsubRef.current) wsStatusUnsubRef.current();
      if (wsPriceUnsubRef.current) wsPriceUnsubRef.current();
      if (wsErrorUnsubRef.current) wsErrorUnsubRef.current();
      if (mdStoreUnsubRef.current) mdStoreUnsubRef.current();
      fivePaisaWebSocketService.disconnect({ manual: false });
    };
  }, [activeBrokerFlag]);

  useEffect(() => {
    if (activeBrokerFlag !== "angelone") {
      if (angelPositionSubscriptionsRef.current.length) {
        angelOneWebSocketService.unsubscribeMarketFeed(
          angelPositionSubscriptionsRef.current,
        );
        angelPositionSubscriptionsRef.current = [];
      }
      return undefined;
    }

    angelWsPriceUnsubRef.current = angelOneWebSocketService.onPrice((tick) => {
      if (tick) handleLivePriceUpdate(tick);
    });
    angelWsErrorUnsubRef.current = angelOneWebSocketService.onError((error) => {
      console.error("❌ AngelOne WS service error:", error);
    });

    angelOneWebSocketService.connect();

    return () => {
      angelWsPriceUnsubRef.current?.();
      angelWsErrorUnsubRef.current?.();
      angelWsPriceUnsubRef.current = null;
      angelWsErrorUnsubRef.current = null;

      if (angelPositionSubscriptionsRef.current.length) {
        angelOneWebSocketService.unsubscribeMarketFeed(
          angelPositionSubscriptionsRef.current,
        );
        angelPositionSubscriptionsRef.current = [];
      }
    };
  }, [activeBrokerFlag]);

  useEffect(() => {
    if (activeBrokerFlag !== "angelone") {
      angelOneMarketDepthService.closeDepth();
      return undefined;
    }

    angelMdServiceUnsubRef.current = angelOneMarketDepthService.subscribe(
      (depth) => {
        setMarketDepth(Array.isArray(depth) ? depth : []);
      },
    );

    return () => {
      angelMdServiceUnsubRef.current?.();
      angelMdServiceUnsubRef.current = null;
      angelOneMarketDepthService.closeDepth();
    };
  }, [activeBrokerFlag]);

  useEffect(() => {
    if (activeBrokerFlag !== "grow") {
      growwLivePriceService.stopPolling(GROW_POLL_SOURCE);
      return undefined;
    }

    const symbols = holdings
      .map((holding) => {
        const symbol = String(holding?.Symbol || holding?.ScripName || "")
          .trim()
          .toUpperCase();
        if (!symbol) return "";
        const exchangePrefix = holding?.Exch === "B" ? "BSE" : "NSE";
        return `${exchangePrefix}_${symbol}`;
      })
      .filter(Boolean);

    growwLivePriceService.setSymbols(GROW_POLL_SOURCE, symbols);
    const unsubscribe = growwLivePriceService.subscribe((payload) => {
      let updated = false;

      holdings.forEach((holding) => {
        const symbol = String(holding?.Symbol || holding?.ScripName || "")
          .trim()
          .toUpperCase();
        if (!symbol) return;

        const exchangePrefix = holding?.Exch === "B" ? "BSE" : "NSE";
        const key = `${exchangePrefix}_${symbol}`;
        const ltp = Number(payload?.[key]);

        if (Number.isFinite(ltp)) {
          const scripCode = holding?.ScripCode || holding?.scripcode;
          if (scripCode) {
            livePriceCache.current[scripCode] = ltp;
            updated = true;
          }
        }
      });

      if (updated) {
        setLiveUpdateTrigger((prev) => prev + 1);
      }
    });

    growwLivePriceService.startPolling(2000);

    return () => {
      unsubscribe?.();
      growwLivePriceService.stopPolling(GROW_POLL_SOURCE);
    };
  }, [activeBrokerFlag, holdings]);

  useEffect(() => {
    document.title = tabName;
  }, [tabName]);

  // MarketDepth
  useEffect(() => {
    const modalEl = document.getElementById("MarketDepthModal");
    if (modalEl) {
      // Attach event listener
      modalEl.addEventListener("hidden.bs.modal", handleModalClose);
    }
    // Cleanup when component unmounts
    return () => {
      if (modalEl) {
        modalEl.removeEventListener("hidden.bs.modal", handleModalClose);
      }
    };
  }, []);

  // Keep ref updated
  useEffect(() => {
    currentMDModalRef.current = currentMDModal;
  }, [currentMDModal]);

  useEffect(() => {
    console.log("📉New Modal Data: ", currentMDModal);
  }, [currentMDModal]);

  // Click-outside handler
  useEffect(() => {
    const handleClickOutside = (event) => {
      if (
        marketDepthPopoverRef.current &&
        !marketDepthPopoverRef.current.contains(event.target) &&
        marketDepthButtonRef.current &&
        !marketDepthButtonRef.current.contains(event.target)
      ) {
        closeMarketDepthPopover();
      }
    };

    if (showMarketDepth) {
      document.addEventListener("mousedown", handleClickOutside);
    }

    return () => {
      document.removeEventListener("mousedown", handleClickOutside);
    };
  }, [showMarketDepth]);

  //----------------------------// marketdepth Functions //----------------------------//

  const handleMarketDepthClick = (holding, e) => {
    const button = e.currentTarget;
    const buttonRect = button.getBoundingClientRect();

    const popoverWidth = 480;
    const popoverHeight = 500;
    const padding = 16;

    let left = buttonRect.left - popoverWidth - 12;
    let top = buttonRect.top - 20;

    // Check if popover goes off-screen on the left
    if (left < padding) {
      left = buttonRect.right + 12;
    }

    // Check if popover goes off-screen on top
    if (top < padding) {
      top = buttonRect.top + buttonRect.height + 12;
    }

    // Check if popover goes off-screen on bottom
    if (top + popoverHeight > window.innerHeight - padding) {
      top = window.innerHeight - popoverHeight - padding;
    }

    // Check if popover goes off-screen on right
    if (left + popoverWidth > window.innerWidth - padding) {
      left = window.innerWidth - popoverWidth - padding;
    }

    setMarketDepthPosition({
      top: Math.max(padding, top),
      left: Math.max(padding, left),
    });
    setShowMarketDepth(true);
    setcurrentMDModal(holding);
    if (isAngelOneBroker) {
      const cachedDepth = Array.isArray(holding?.marketDepth)
        ? holding.marketDepth
        : normalizeAngelMarketDepth(holding?.market_quote?.depth);
      if (cachedDepth.length) {
        setMarketDepth(cachedDepth);
      }
    }
    subscribeToMarketDepth(holding);
  };

  //----------------------------// Functions //----------------------------//

  // Notification sound function
  const pingNotification = () => {
    try {
      const audio = new Audio("/sounds/notifications.mp3");
      audio.play().catch((error) => {
        console.error("Failed to play notification sound:", error);
      });
    } catch (error) {
      console.error("Error creating audio:", error);
    }
  };

  // Common toast options
  const toastOptions = {
    position: "top-center",
    autoClose: 4000,
    hideProgressBar: false,
    closeOnClick: true,
    pauseOnHover: true,
    draggable: true,
    theme: "light",
  };

  // Success toast function
  const showSuccessToast = (message) => {
    toast.success(message, {
      ...toastOptions,
      icon: "✅",
    });
    pingNotification();
  };

  // Error toast function
  const showErrorToast = (message) => {
    toast.error(message, {
      ...toastOptions,
      icon: "⚠️",
    });
    pingNotification();
  };

  // Error toast function
  const showWarningToast = (message) => {
    toast.warn(message, {
      ...toastOptions,
      icon: "⚠️",
    });
    pingNotification();
  };

  //----------------------------// table Search Functions //----------------------------//

  // Add this before your tbody
  const filteredHoldings = holdings.filter((holding) => {
    if (!searchQuery) return true;

    const searchLower = searchQuery.toLowerCase();
    const name = (holding.name || "").toLowerCase();
    const fullName = (holding.FullName || "").toLowerCase();

    return name.includes(searchLower) || fullName.includes(searchLower);
  });

  const getPositionSortableValue = useCallback((holding, columnKey) => {
    if (!holding) return "";
    const raw =
      holding?.[columnKey] ??
      holding?.[String(columnKey || "").replace(/\s+/g, "")] ??
      "";
    const num = Number(String(raw).replace(/,/g, ""));
    return Number.isFinite(num) && raw !== "" ? num : String(raw).toLowerCase();
  }, []);

  const sortedHoldings = useMemo(() => {
    if (!sortConfig?.column || !sortConfig?.direction) return filteredHoldings;
    const dir = sortConfig.direction === "asc" ? 1 : -1;
    return [...filteredHoldings].sort((a, b) => {
      const aVal = getPositionSortableValue(a, sortConfig.column);
      const bVal = getPositionSortableValue(b, sortConfig.column);
      if (typeof aVal === "number" && typeof bVal === "number") return (aVal - bVal) * dir;
      return String(aVal).localeCompare(String(bVal), undefined, { numeric: true, sensitivity: "base" }) * dir;
    });
  }, [filteredHoldings, getPositionSortableValue, sortConfig]);

  useEffect(() => {
    if (!activeHeaderMenu) return undefined;
    const handleClose = () => setActiveHeaderMenu(null);
    document.addEventListener("mousedown", handleClose);
    window.addEventListener("scroll", handleClose, true);
    window.addEventListener("resize", handleClose);
    return () => {
      document.removeEventListener("mousedown", handleClose);
      window.removeEventListener("scroll", handleClose, true);
      window.removeEventListener("resize", handleClose);
    };
  }, [activeHeaderMenu]);

  //----------------------------// End table Search Functions //----------------------------//

  // const calculateMTOM = (pos) => {
  //   if (!pos.NetQty || !pos.AvgRate || !pos.LTP) return 0;

  //   // NetQty already reflects buy - sell, positive for long, negative for short
  //   return (pos.LTP - pos.AvgRate) * pos.NetQty * (pos.Multiplier || 1);
  // };

  // const calculateMTOM = (pos) => {
  //   // if (!pos.NetQty || !pos.BuyAvgRate) return 0;
  //   const avgRate = pos.BuyAvgRate || pos.AvgRate;
  //   if (!pos.NetQty || !avgRate) return 0;

  //   // Get live price from cache, fallback to LTP
  //   const scripCode = pos.ScripCode || pos.scripcode;
  //   const livePrice = parseFloat(livePriceCache.current?.[scripCode]);
  //   const priceToUse = !isNaN(livePrice) ? livePrice : parseFloat(pos.LTP || 0);

  //   // NetQty already reflects buy - sell, positive for long, negative for short
  //   return (priceToUse - pos.BuyAvgRate) * pos.NetQty * (pos.Multiplier || 1);
  //   // return ((priceToUse * pos.NetQty)-(avgRate * pos.NetQty))
  // };

  const calculateMTOM = (pos) => {
    const avgRate = pos.BuyAvgRate || pos.AvgRate;
    if (!pos.NetQty || !avgRate) return 0;

    // Get live price from cache, fallback to LTP
    const scripCode =
      pos.ScripCode || pos.scripcode || pos.token || pos.symboltoken;
    const livePrice = parseFloat(livePriceCache.current?.[scripCode]);
    const priceToUse = !isNaN(livePrice) ? livePrice : parseFloat(pos.LTP || 0);

    // MTOM = (CurrentPrice - AvgRate) * NetQty * LotSize
    // return (priceToUse - avgRate) * pos.NetQty * (pos.LotSize || 1);
    return (priceToUse - avgRate) * pos.NetQty * (pos.Multiplier || 1);
  };

  // const calculateMTOM = (pos) => {
  //   // Use BuyAvgRate if available, otherwise fallback to AvgRate
  //   const avgRate = pos.BuyAvgRate || pos.AvgRate;
  //   if (!pos.NetQty || !avgRate) return 0;

  //   // Get live price from cache, fallback to LTP
  //   const scripCode = pos.ScripCode || pos.scripcode;
  //   const livePrice = parseFloat(livePriceCache.current?.[scripCode]);
  //   const priceToUse = !isNaN(livePrice) ? livePrice : parseFloat(pos.LTP || 0);

  //   // MTOM = (currentPrice - averagePrice) * NetQty * Multiplier
  //   return (priceToUse - avgRate) * pos.NetQty * (pos.Multiplier || 1);
  // };

  const subscribePositionFeeds = (positions = []) => {
    const marketFeedItems = positions
      .map((position) => ({
        Exch: position?.Exch,
        ExchType: position?.ExchType,
        ScripCode: position?.ScripCode,
      }))
      .filter((item) => item.Exch && item.ExchType && item.ScripCode);

    if (marketFeedItems.length) {
      fivePaisaWebSocketService.subscribeMarketFeed(marketFeedItems);
    }
  };

  const subscribeAngelPositionFeeds = (positions = []) => {
    if (angelPositionSubscriptionsRef.current.length) {
      angelOneWebSocketService.unsubscribeMarketFeed(
        angelPositionSubscriptionsRef.current,
      );
      angelPositionSubscriptionsRef.current = [];
    }

    const seenTokens = new Set();
    const feedItems = [];

    positions.forEach((position) => {
      const token = String(
        position?.token || position?.symboltoken || position?.ScripCode || "",
      ).trim();
      if (!token || seenTokens.has(token)) return;

      const exchangeType = getAngelExchangeTypeFromHolding({
        Exch: position?.Exch,
        ExchSeg: position?.ExchSeg || position?.exchange,
        ExchType: position?.ExchType,
        InstrumentType:
          position?.instrumenttype ||
          position?.InstrumentType ||
          position?.segment ||
          "",
      });

      if (!exchangeType) return;

      seenTokens.add(token);
      feedItems.push({ exchangeType, token });
    });

    if (!feedItems.length) return;

    angelOneWebSocketService.connect();
    for (let i = 0; i < feedItems.length; i += 50) {
      angelOneWebSocketService.subscribeMarketFeed(feedItems.slice(i, i + 50));
    }
    angelPositionSubscriptionsRef.current = feedItems;
  };

  // Send Message to WebSocket
  // const sendMessageToWebSocket = (holding, clientCode, status = "Portfolio") => {
  //   if (!ws || ws.readyState !== WebSocket.OPEN) {
  //     console.warn("WebSocket is not connected");
  //     return;
  //   }

  //   let ScripCode = 0;
  //   if (status === "Search") {
  //     ScripCode = holding.Scripcode;
  //   } else {
  //     ScripCode = holding.Exch === "N" ? holding.NseCode : holding.BseCode;
  //   }

  //   const priceMessage = {
  //     Method: "MarketFeedV3",
  //     Operation: "Subscribe",
  //     ClientCode: clientCode,
  //     MarketFeedData: [
  //       {
  //         Exch: holding.Exch.charAt(0),
  //         ExchType: holding.ExchType.charAt(0),
  //         ScripCode: ScripCode,
  //       },
  //     ],
  //   };

  //   const depthMessage = {
  //     Method: "MarketDepthService",
  //     Operation: "Subscribe",
  //     ClientCode: clientCode,
  //     MarketFeedData: [
  //       {
  //         Exch: holding.Exch.charAt(0),
  //         ExchType: holding.ExchType.charAt(0),
  //         ScripCode: ScripCode,
  //       },
  //     ],
  //   };

  //   try {
  //     ws.send(JSON.stringify(priceMessage));
  //     ws.send(JSON.stringify(depthMessage));
  //     console.log(`✅ Subscribed to ${holding.Symbol} (${ScripCode})`);

  //     if (!subscribedScripCodes.current) {
  //       subscribedScripCodes.current = new Set();
  //     }
  //     subscribedScripCodes.current.add(ScripCode);
  //   } catch (error) {
  //     console.error("Error sending WebSocket message:", error);
  //   }
  // };


  // Handle Live Price Updates
  // const handleLivePriceUpdate = (data) => {
  //   const { Token: scriptCode, LastRate: lastRateValue, PClose, Volume } = data;

  //   if (!scriptCode || lastRateValue === undefined) return;

  //   if (!livePriceCache.current) livePriceCache.current = {};
  //   if (!livePCloseCache.current) livePCloseCache.current = {};
  //   if (!liveDiffCache.current) liveDiffCache.current = {};
  //   if (!liveChangePercentageCache.current) liveChangePercentageCache.current = {};
  //   if (!liveVolumeCache.current) liveVolumeCache.current = {};
  //   if (!livePLCache.current) livePLCache.current = {};

  //   livePriceCache.current[scriptCode] = lastRateValue;
  //   livePCloseCache.current[scriptCode] = PClose;
  //   liveDiffCache.current[scriptCode] = lastRateValue - PClose;
  //   liveChangePercentageCache.current[scriptCode] = ((lastRateValue - PClose) / PClose) * 100;
  //   liveVolumeCache.current[scriptCode] = Volume;

  //   const totalPL = lastRateValue - PClose;
  //   livePLCache.current[scriptCode] = `${totalPL.toFixed(2)} (${liveChangePercentageCache.current[scriptCode].toFixed(2)}%)`;

  //   setLiveUpdateTrigger((prev) => prev + 1);
  //   console.log(`📈 Price Update: ${scriptCode} = ₹${lastRateValue}`);
  // };
  // Handle Live Price Updates
  const handleLivePriceUpdate = (data) => {
    const {
      Token: scriptCode,
      LastRate: lastRateValue,
      PClose,
      Volume,
      High, // ✅ Added
      Low, // ✅ Added
    } = data;

    if (!scriptCode || lastRateValue === undefined) return;

    if (!livePriceCache.current) livePriceCache.current = {};
    if (!livePCloseCache.current) livePCloseCache.current = {};
    if (!liveDiffCache.current) liveDiffCache.current = {};
    if (!liveChangePercentageCache.current)
      liveChangePercentageCache.current = {};
    if (!liveVolumeCache.current) liveVolumeCache.current = {};
    if (!livePLCache.current) livePLCache.current = {};
    if (!liveHighCache.current) liveHighCache.current = {}; // ✅ Added
    if (!liveLowCache.current) liveLowCache.current = {}; // ✅ Added

    livePriceCache.current[scriptCode] = lastRateValue;
    livePCloseCache.current[scriptCode] = PClose;
    liveDiffCache.current[scriptCode] = lastRateValue - PClose;
    liveChangePercentageCache.current[scriptCode] =
      ((lastRateValue - PClose) / PClose) * 100;
    liveVolumeCache.current[scriptCode] = Volume;
    liveHighCache.current[scriptCode] = High; // ✅ Added
    liveLowCache.current[scriptCode] = Low; // ✅ Added

    const totalPL = lastRateValue - PClose;
    livePLCache.current[scriptCode] =
      `${totalPL.toFixed(2)} (${liveChangePercentageCache.current[scriptCode].toFixed(2)}%)`;

    setLiveUpdateTrigger((prev) => prev + 1);
    console.log(
      `📈 [${scriptCode}] LTP ₹${lastRateValue} | High ₹${High} | Low ₹${Low}`,
    ); // ✅ Updated
  };

  class BasePositionAdapter {
    numberOrZero = (value) => {
      const num = Number(value);
      return Number.isFinite(num) ? num : 0;
    };

    stringOrDefault = (value, fallback = "") =>
      value === null || value === undefined || value === "" ? fallback : String(value);

    pickFirst = (...values) => values.find((v) => v !== null && v !== undefined && v !== "");

    pickLatestTarget = (targetData = []) => {
      if (!Array.isArray(targetData) || targetData.length === 0) return {};
      return [...targetData]
        .filter((item) => item && typeof item === "object")
        .sort(
          (a, b) =>
            new Date(b?.created_at || 0).getTime() -
            new Date(a?.created_at || 0).getTime(),
        )[0] || {};
    };

    normalizeTargetFields = (holding = {}) => {
      const hasTargetData =
        Array.isArray(holding?.target_data) && holding.target_data.length > 0;
      const target = this.pickLatestTarget(holding?.target_data);

      return {
        hasTargetData,
        initialPrice: this.numberOrZero(
          this.pickFirst(
            holding?.initialPrice,
            holding?.initial_price,
            target?.initialPrice,
            target?.initial_price,
          ),
        ),
        targetPrice: this.numberOrZero(
          this.pickFirst(holding?.targetPrice, target?.targetPrice),
        ),
        target_logic: this.stringOrDefault(
          this.pickFirst(holding?.target_logic, target?.target_logic),
          "",
        ),
        targetReason: this.stringOrDefault(
          this.pickFirst(
            holding?.targetReason,
            holding?.target_reason,
            target?.targetReason,
            target?.target_reason,
          ),
          "",
        ),
        targetReasonDetails: this.stringOrDefault(
          this.pickFirst(
            holding?.targetReasonDetails,
            holding?.target_reason_details,
            target?.targetReasonDetails,
            target?.target_reason_details,
          ),
          "",
        ),
        source: this.stringOrDefault(
          this.pickFirst(holding?.source, target?.source),
          "position",
        ),
        source_watchlist: this.stringOrDefault(
          this.pickFirst(holding?.source_watchlist, target?.source_watchlist),
          "",
        ),
        createdDate: this.stringOrDefault(
          this.pickFirst(holding?.createdDate, target?.created_at),
          "",
        ),
      };
    };

    normalizePositions = (positions = []) => positions;
  }

  class FivePaisaPositionAdapter extends BasePositionAdapter {
    normalizePositions = (positions = []) =>
      positions.map((position = {}) => {
        const exch = this.stringOrDefault(position?.Exch, "N").charAt(0).toUpperCase();
        const exchType = this.stringOrDefault(position?.ExchType, "C").charAt(0).toUpperCase();
        const symbol = this.stringOrDefault(
          this.pickFirst(position?.Symbol, position?.ScripName),
          "N/A",
        );

        return {
          ...position,
          Exch: exch,
          ExchType: exchType,
          ScripCode: this.numberOrZero(position?.ScripCode),
          Symbol: symbol,
          ScripName: this.stringOrDefault(position?.ScripName, symbol),
          FullName: this.stringOrDefault(
            this.pickFirst(position?.FullName, position?.name, position?.ScripName),
            symbol,
          ),
          name: this.stringOrDefault(
            this.pickFirst(position?.name, position?.FullName, position?.ScripName),
            symbol,
          ),
          NetQty: this.numberOrZero(position?.NetQty),
          BuyQty: this.numberOrZero(position?.BuyQty),
          SellQty: this.numberOrZero(position?.SellQty),
          BodQty: this.numberOrZero(position?.BodQty),
          BuyAvgRate: this.numberOrZero(position?.BuyAvgRate),
          SellAvgRate: this.numberOrZero(position?.SellAvgRate),
          AvgRate: this.numberOrZero(position?.AvgRate),
          BuyValue: this.numberOrZero(position?.BuyValue),
          SellValue: this.numberOrZero(position?.SellValue),
          LTP: this.numberOrZero(position?.LTP),
          PreviousClose: this.numberOrZero(position?.PreviousClose),
          TodaysHigh: this.numberOrZero(position?.TodaysHigh),
          TodaysLow: this.numberOrZero(position?.TodaysLow),
          Multiplier: this.numberOrZero(position?.Multiplier || 1),
          ...this.normalizeTargetFields(position),
        };
      });
  }

  class GrowPositionAdapter extends BasePositionAdapter {
    normalizePositions = (positions = []) =>
      positions.map((position = {}) => {
        const stocksData = position?.stocks_data || {};
        const ohlc = stocksData?.ohlc || {};
        const exchRaw = this.stringOrDefault(
          this.pickFirst(position?.exchange, position?.Exchange, position?.Exch, "NSE"),
          "NSE",
        ).toUpperCase();
        const exch = exchRaw.startsWith("B") ? "B" : "N";
        const exchType = this.stringOrDefault(
          this.pickFirst(position?.exchange_type, position?.ExchType, "C"),
          "C",
        ).charAt(0).toUpperCase();
        const symbol = this.stringOrDefault(
          this.pickFirst(position?.trading_symbol, position?.Symbol, position?.ScripName, position?.symbol),
          "N/A",
        );
        const uniqueCode = this.stringOrDefault(
          this.pickFirst(
            position?.ScripCode,
            position?.scripcode,
            position?.instrument_token,
            position?.instrumentToken,
            position?.isin,
            symbol,
          ),
          symbol,
        );
        const ltp = this.numberOrZero(
          this.pickFirst(
            position?.LTP,
            position?.ltp,
            position?.last_price,
            position?.current_price,
            stocksData?.close,
            stocksData?.LastRate,
            stocksData?.last_price,
            ohlc?.close,
            stocksData?.high,
            stocksData?.open,
            ohlc?.open,
            position?.net_price,
          ),
        );
        const buyQty = this.numberOrZero(
          this.pickFirst(
            position?.BuyQty,
            position?.buy_qty,
            position?.buy_quantity,
            position?.credit_quantity,
          ),
        );
        const sellQty = this.numberOrZero(
          this.pickFirst(
            position?.SellQty,
            position?.sell_qty,
            position?.sell_quantity,
            position?.debit_quantity,
          ),
        );
        const netQty = this.numberOrZero(
          this.pickFirst(
            position?.NetQty,
            position?.net_qty,
            position?.quantity,
            buyQty - sellQty,
          ),
        );
        const buyAvgRate = this.numberOrZero(
          this.pickFirst(
            position?.BuyAvgRate,
            position?.buy_avg_price,
            position?.avg_buy_price,
            position?.credit_price,
            position?.AvgRate,
          ),
        );
        const sellAvgRate = this.numberOrZero(
          this.pickFirst(
            position?.SellAvgRate,
            position?.sell_avg_price,
            position?.avg_sell_price,
            position?.debit_price,
          ),
        );
        const avgRate = this.numberOrZero(this.pickFirst(position?.AvgRate, buyAvgRate));

        return {
          ...position,
          Exch: exch,
          ExchType: exchType,
          ScripCode: uniqueCode,
          Symbol: symbol,
          ScripName: this.stringOrDefault(position?.ScripName, symbol),
          FullName: this.stringOrDefault(
            this.pickFirst(position?.FullName, position?.name, position?.company_name, symbol),
            symbol,
          ),
          name: this.stringOrDefault(
            this.pickFirst(position?.name, position?.FullName, position?.company_name, symbol),
            symbol,
          ),
          NetQty: netQty,
          BuyQty: buyQty,
          SellQty: sellQty,
          BodQty: this.numberOrZero(this.pickFirst(position?.BodQty, position?.bod_qty, buyQty)),
          BuyAvgRate: buyAvgRate,
          SellAvgRate: sellAvgRate,
          AvgRate: avgRate,
          BuyValue: this.numberOrZero(
            this.pickFirst(
              position?.BuyValue,
              position?.buy_value,
              buyQty * buyAvgRate,
            ),
          ),
          SellValue: this.numberOrZero(
            this.pickFirst(position?.SellValue, position?.sell_value, sellQty * sellAvgRate),
          ),
          LTP: ltp,
          PreviousClose: this.numberOrZero(
            this.pickFirst(
              position?.PreviousClose,
              position?.previous_close,
              stocksData?.PClose,
              stocksData?.previous_close,
              stocksData?.pc,
              ohlc?.close,
              stocksData?.open,
              ohlc?.open,
              position?.close,
              position?.previousClose,
              ltp,
            ),
          ),
          TodaysHigh: this.numberOrZero(
            this.pickFirst(
              position?.TodaysHigh,
              position?.day_high,
              position?.high,
              stocksData?.high,
              stocksData?.High,
              ohlc?.high,
            ),
          ),
          TodaysLow: this.numberOrZero(
            this.pickFirst(
              position?.TodaysLow,
              position?.day_low,
              position?.low,
              stocksData?.low,
              stocksData?.Low,
              ohlc?.low,
            ),
          ),
          Multiplier: this.numberOrZero(this.pickFirst(position?.Multiplier, position?.multiplier, 1)),
          ...this.normalizeTargetFields(position),
        };
      });
  }

  class AngelOnePositionAdapter extends BasePositionAdapter {
    parseTradingSymbol = (tradingsymbol) => {
      const raw = this.stringOrDefault(tradingsymbol, "N/A");
      const base = raw.includes("-") ? raw.split("-")[0] : raw;
      return { raw, base: this.stringOrDefault(base, raw) };
    };

    normalizeMultiplier = (value) => {
      const multiplier = this.numberOrZero(value);
      return multiplier > 0 ? multiplier : 1;
    };

    normalizePositions = (positions = []) =>
      positions.map((position = {}) => {
        const token = this.stringOrDefault(position?.symboltoken, "");
        const scripCode = this.numberOrZero(token);
        const { raw: tradingSymbol, base: symbolBase } = this.parseTradingSymbol(
          position?.tradingsymbol,
        );
        const exchRaw = this.stringOrDefault(position?.exchange, "NSE").toUpperCase();
        const exch = exchRaw.startsWith("B")
          ? "B"
          : exchRaw.startsWith("M")
            ? "M"
            : "N";
        const productType = this.stringOrDefault(
          position?.producttype,
          "DELIVERY",
        ).toUpperCase();
        const instrumentType = this.stringOrDefault(
          position?.instrumenttype,
          "",
        ).toUpperCase();
        const exchType =
          productType.includes("DELIVERY") || productType === "CNC" ? "C" : "D";
        const symbolName = this.stringOrDefault(
          position?.symbolname,
          symbolBase,
        );
        const ltp = this.numberOrZero(position?.ltp);
        const previousClose = this.numberOrZero(position?.close);
        const buyQty = this.numberOrZero(position?.buyqty);
        const sellQty = this.numberOrZero(position?.sellqty);
        const netQty = this.numberOrZero(
          this.pickFirst(position?.netqty, buyQty - sellQty),
        );
        const buyAvgRate = this.numberOrZero(
          this.pickFirst(
            position?.buyavgprice,
            position?.totalbuyavgprice,
            position?.avgnetprice,
          ),
        );
        const sellAvgRate = this.numberOrZero(
          this.pickFirst(
            position?.sellavgprice,
            position?.totalsellavgprice,
          ),
        );
        const avgRate = this.numberOrZero(
          this.pickFirst(position?.avgnetprice, position?.netprice, buyAvgRate),
        );

        return {
          ...position,
          Exch: exch,
          ExchType: exchType,
          ExchSeg: exchRaw,
          ScripCode: scripCode,
          scripcode: token,
          token,
          Token: token,
          Symbol: symbolBase || tradingSymbol,
          ScripName: tradingSymbol,
          TradingSymbol: tradingSymbol,
          tradingsymbol: tradingSymbol,
          FullName: symbolName,
          name: symbolName,
          symboltoken: token,
          InstrumentType: instrumentType,
          NetQty: netQty,
          BuyQty: buyQty,
          SellQty: sellQty,
          BodQty: buyQty,
          BuyAvgRate: buyAvgRate,
          SellAvgRate: sellAvgRate,
          AvgRate: avgRate,
          BuyValue: this.numberOrZero(
            this.pickFirst(position?.totalbuyvalue, position?.buyamount),
          ),
          SellValue: this.numberOrZero(
            this.pickFirst(position?.totalsellvalue, position?.sellamount),
          ),
          LTP: ltp,
          PreviousClose: previousClose,
          TodaysHigh: 0,
          TodaysLow: 0,
          Multiplier: this.normalizeMultiplier(position?.multiplier),
          PnL: this.numberOrZero(position?.pnl),
          UnrealisedPnL: this.numberOrZero(position?.unrealised),
          RealisedPnL: this.numberOrZero(position?.realised),
          ...this.normalizeTargetFields(position),
        };
      });
  }

  const getPositionAdapter = (brokerFlag) => {
    const normalizedFlag = String(brokerFlag || "").toLowerCase();
    if (normalizedFlag === "grow") return new GrowPositionAdapter();
    if (normalizedFlag === "5paisa") return new FivePaisaPositionAdapter();
    if (normalizedFlag === "angelone") return new AngelOnePositionAdapter();
    return new BasePositionAdapter();
  };

  // const fetchPositions = async (AccessToken, ClientCode, api_key) => {
  //   const url = "https://Openapi.5paisa.com/VendorsAPI/Service1.svc/V2/NetPositionNetWise";
  //   const data = {
  //     head: { key: api_key },
  //     body: { ClientCode: ClientCode },
  //   };

  //   try {
  //     const response = await fetch(url, {
  //       method: "POST",
  //       headers: {
  //         "Content-Type": "application/json",
  //         Authorization: `bearer ${AccessToken}`,
  //       },
  //       body: JSON.stringify(data),
  //     });

  //     let responseData;
  //     try {
  //       responseData = await response.json();
  //     } catch (parseError) {
  //       console.warn("Non-JSON response received:", await response.text());
  //       setHoldings([]);
  //       return;
  //     }

  //     const positions = responseData.body?.NetPositionDetail || [];
  //     setHoldings(positions);
  //     setIsLoading(false);
  //     console.log("Initial Positions --> :", positions);

  //     // 🔥 Connect to WebSocket after fetching positions
  //     handleConnectClick();

  //     if (responseData.head?.status === "0" && positions.length > 0) {
  //       // Update subscribed stocks state for WebSocket
  //       setSubscribedStocks((prev) => {
  //         const newStocks = positions.map((item) => ({
  //           Scripcode: item.ScripCode,
  //           Exch: item.Exch,
  //           ExchType: item.ExchType,
  //           Symbol: item.ScripName,
  //         }));
  //         const merged = [...prev, ...newStocks];
  //         const unique = merged.filter(
  //           (v, i, a) => a.findIndex((t) => t.Scripcode === v.Scripcode) === i
  //         );
  //         return unique;
  //       });

  //       // 🔥 Subscribe each position to WebSocket - Wait for connection
  //       const subscribeWhenReady = () => {
  //         // Store positions for resubscription after reconnects
  //         lastSubscribedHoldings.current = positions;

  //         const checkConnection = () => {
  //           if (wsRef.current && wsRef.current.readyState === WebSocket.OPEN) {
  //             console.log("🚀 WebSocket ready, subscribing to positions...");
  //             positions.forEach((position) => {
  //               sendMessageToWebSocket(position, ClientCode, "Position");
  //             });
  //           } else {
  //             console.log("⏳ Waiting for WebSocket connection...");
  //             setTimeout(checkConnection, 500);
  //           }
  //         };

  //         // Start checking after 1 second
  //         setTimeout(checkConnection, 1000);
  //       };
  //       subscribeWhenReady();
  //     }
  //     setProgress(100);
  //   } catch (error) {
  //     console.error("Error fetching positions:", error);
  //     setHoldings([]);
  //     setIsLoading(false);
  //     setProgress(100);
  //   }
  // };

  //----------------------------// Return Main //----------------------------//

  //   const fetchPositions = async (AccessToken, ClientCode, api_key) => {
  //   const url = "https://Openapi.5paisa.com/VendorsAPI/Service1.svc/V2/NetPositionNetWise";
  //   const data = {
  //     head: { key: api_key },
  //     body: { ClientCode: ClientCode },
  //   };

  //   try {
  //     const response = await fetch(url, {
  //       method: "POST",
  //       headers: {
  //         "Content-Type": "application/json",
  //         Authorization: `bearer ${AccessToken}`,
  //       },
  //       body: JSON.stringify(data),
  //     });

  //     let responseData;
  //     try {
  //       responseData = await response.json();
  //     } catch (parseError) {
  //       console.warn("Non-JSON response received:", await response.text());
  //       setHoldings([]);
  //       return;
  //     }

  //     const positions = responseData.body?.NetPositionDetail || [];
  //     console.log("Initial Positions --> :", positions);

  //     // Update livePriceCache here
  //     positions.forEach((position) => {
  //       if (position.ScripCode) {
  //         livePriceCache.current[position.ScripCode] = formatNumber(position.LTP);
  //       }
  //     });
  //     console.log("livePriceCache after fetch:", livePriceCache.current);

  //     // 🔹 Call your PHP API to enrich with target/initialPrice
  //     try {
  //       const enrichResponse = await fetch(
  //         `https://beta.infomanav.in/keep/zerodhaAPI/api.php?xAction=getPositionsWithTargets`,
  //         {
  //           method: "POST",
  //           headers: { "Content-Type": "application/json" },
  //           body: JSON.stringify({
  //             userId: sharedData.ID,   // backend expects this
  //             positions: positions,
  //           }),
  //         }
  //       );

  //       const enrichData = await enrichResponse.json();
  //       console.log("enriched positions -->", enrichData);

  //       if (enrichData.status === "success") {
  //         setHoldings(enrichData.positions);
  //       } else {
  //         // fallback if API fails
  //         setHoldings(positions);
  //       }
  //     } catch (err) {
  //       console.error("Error enriching positions:", err);
  //       setHoldings(positions);
  //     }

  //     setIsLoading(false);

  //     // 🔥 Connect to WebSocket after fetching positions
  //     handleConnectClick();

  //     if (responseData.head?.status === "0" && positions.length > 0) {
  //       // Update subscribed stocks state for WebSocket
  //       setSubscribedStocks((prev) => {
  //         const newStocks = positions.map((item) => ({
  //           Scripcode: item.ScripCode,
  //           Exch: item.Exch,
  //           ExchType: item.ExchType,
  //           Symbol: item.ScripName,
  //         }));
  //         const merged = [...prev, ...newStocks];
  //         const unique = merged.filter(
  //           (v, i, a) => a.findIndex((t) => t.Scripcode === v.Scripcode) === i
  //         );
  //         return unique;
  //       });

  //       // 🔥 Subscribe each position to WebSocket - Wait for connection
  //       const subscribeWhenReady = () => {
  //         // Store positions for resubscription after reconnects
  //         lastSubscribedHoldings.current = positions;

  //         const checkConnection = () => {
  //           if (wsRef.current && wsRef.current.readyState === WebSocket.OPEN) {
  //             console.log("🚀 WebSocket ready, subscribing to positions...");
  //             positions.forEach((position) => {
  //               sendMessageToWebSocket(position, ClientCode, "Position");
  //             });
  //           } else {
  //             console.log("⏳ Waiting for WebSocket connection...");
  //             setTimeout(checkConnection, 500);
  //           }
  //         };

  //         // Start checking after 1 second
  //         setTimeout(checkConnection, 1000);
  //       };
  //       subscribeWhenReady();
  //     }
  //     setProgress(100);
  //   } catch (error) {
  //     console.error("Error fetching positions:", error);
  //     setHoldings([]);
  //     setIsLoading(false);
  //     setProgress(100);
  //   }
  // };

  // const fetchPositions = async (AccessToken, ClientCode, api_key) => {

  //   let columnsToUse = columns || []; // fallback to existing columns state

  //   // Fetch column order from API first
  //   try {
  //     const colOrderResponse = await fetch("https://beta.infomanav.in/keep/zerodhaAPI/5paisa_columnOrder_2/get_col_order_and_visibility.php", {
  //       method: "POST",
  //       headers: { "Content-Type": "application/json" },
  //       body: JSON.stringify({ module_name: "Position", user_id: sharedData.ID }),
  //     });

  //     const colOrderResult = await colOrderResponse.json();

  //     if (colOrderResult.status === "success" && Array.isArray(colOrderResult.columnOrder)) {
  //       // Add "Actions" column if missing
  //       setColumnOrder(colOrderResult.columnOrder);
  //       if (!colOrderResult.columnOrder.includes("Actions")) {
  //         colOrderResult.columnOrder.push("Actions");
  //       }
  //       setColumns(colOrderResult.columnOrder);
  //       columnsToUse = colOrderResult.columnOrder; // use updated columns immediately
  //     } else {
  //       console.warn("Failed to fetch column order, using default");
  //     }
  //   } catch (error) {
  //     console.error("Error fetching column order:", error);
  //   }

  //   console.log("✅📉Columns is: ", columnsToUse);

  //   const url = "https://Openapi.5paisa.com/VendorsAPI/Service1.svc/V2/NetPositionNetWise";
  //   const data = {
  //     head: { key: api_key },
  //     body: { ClientCode: ClientCode },
  //   };

  //   try {
  //     const response = await fetch(url, {
  //       method: "POST",
  //       headers: {
  //         "Content-Type": "application/json",
  //         Authorization: `bearer ${AccessToken}`,
  //       },
  //       body: JSON.stringify(data),
  //     });

  //     let responseData;
  //     try {
  //       responseData = await response.json();
  //     } catch (parseError) {
  //       console.warn("Non-JSON response received:", await response.text());
  //       setHoldings([]);
  //       return;
  //     }

  //     const positions = responseData.body?.NetPositionDetail || [];

  //     // 🔥 Transform each position by adding FullName and name properties
  //     const transformedPositions = positions.map(position => ({
  //       ...position,
  //       FullName: position.ScripName || "",
  //       name: position.ScripName || ""
  //     }));

  //     console.log("Initial Positions --> :", transformedPositions);

  //     // Update livePriceCache here
  //     transformedPositions.forEach((position) => {
  //       if (position.ScripCode) {
  //         livePriceCache.current[position.ScripCode] = formatNumber(position.LTP);
  //       }
  //     });
  //     console.log("livePriceCache after fetch:", livePriceCache.current);

  //     // 🔹 Call your PHP API to enrich with target/initialPrice
  //     try {
  //       const enrichResponse = await fetch(
  //         `https://beta.infomanav.in/keep/zerodhaAPI/api.php?xAction=getPositionsWithTargets`,
  //         {
  //           method: "POST",
  //           headers: { "Content-Type": "application/json" },
  //           body: JSON.stringify({
  //             userId: sharedData.ID,   // backend expects this
  //             positions: transformedPositions, // Use transformed positions
  //           }),
  //         }
  //       );

  //       const enrichData = await enrichResponse.json();
  //       console.log("enriched positions -->", enrichData);

  //       if (enrichData.status === "success") {
  //         // 🔥 Also transform enriched positions in case they don't have these properties
  //         const enrichedTransformed = enrichData.positions.map(position => ({
  //           ...position,
  //           FullName: position.FullName || position.ScripName || "",
  //           name: position.name || position.ScripName || ""
  //         }));
  //         setHoldings(enrichedTransformed);
  //       } else {
  //         // fallback if API fails
  //         setHoldings(transformedPositions);
  //       }
  //     } catch (err) {
  //       console.error("Error enriching positions:", err);
  //       setHoldings(transformedPositions);
  //     }

  //     // setIsLoading(false);
  //     setTimeout(() => {
  //       setIsLoading(false);
  //     }, 1500);

  //     // 🔥 Connect to WebSocket after fetching positions
  //     handleConnectClick();

  //     if (responseData.head?.status === "0" && transformedPositions.length > 0) {
  //       // Update subscribed stocks state for WebSocket
  //       setSubscribedStocks((prev) => {
  //         const newStocks = transformedPositions.map((item) => ({
  //           Scripcode: item.ScripCode,
  //           Exch: item.Exch,
  //           ExchType: item.ExchType,
  //           Symbol: item.ScripName,
  //         }));
  //         const merged = [...prev, ...newStocks];
  //         const unique = merged.filter(
  //           (v, i, a) => a.findIndex((t) => t.Scripcode === v.Scripcode) === i
  //         );
  //         return unique;
  //       });

  //       // 🔥 Subscribe each position to WebSocket - Wait for connection
  //       const subscribeWhenReady = () => {
  //         // Store positions for resubscription after reconnects
  //         lastSubscribedHoldings.current = transformedPositions;

  //         const checkConnection = () => {
  //           if (wsRef.current && wsRef.current.readyState === WebSocket.OPEN) {
  //             console.log("🚀 WebSocket ready, subscribing to positions...");
  //             transformedPositions.forEach((position) => {
  //               sendMessageToWebSocket(position, ClientCode, "Position");
  //             });
  //           } else {
  //             console.log("⏳ Waiting for WebSocket connection...");
  //             setTimeout(checkConnection, 500);
  //           }
  //         };

  //         // Start checking after 1 second
  //         setTimeout(checkConnection, 1000);
  //       };
  //       subscribeWhenReady();
  //     }
  //     setProgress(100);
  //   } catch (error) {
  //     console.error("Error fetching positions:", error);
  //     setHoldings([]);
  //     setIsLoading(false);
  //     setProgress(100);
  //   }
  // };

  const fetchPositions = async (AccessToken, ClientCode, api_key) => {
    const fetchId = ++positionFetchIdRef.current;
    let columnsToUse = columns || [];

    const userEmail =
      sharedData?.Email_ID ||
      sharedData?.email ||
      localStorage.getItem("email") ||
      "";
    const brokerFlag = toPmsBrokerFlag(activeBrokerFlag);

    // Column order from PMS (module_name must stay lowercase: "position")
    try {
      if (userEmail) {
        const colOrderResponse = await fetch(
          `${pmsApiBaseUrl}/api/v1/columns/list`,
          {
            method: "POST",
            headers: { "Content-Type": "application/json" },
            body: JSON.stringify({
              broker_flag: brokerFlag,
              module_name: "position",
              user_id: Number(sharedData?.ID || localStorage.getItem("id") || 0),
              email: userEmail,
            }),
          },
        );

        const colOrderPayload = await colOrderResponse.json();
        const colData = colOrderPayload?.data;
        const apiColumns = colData?.columns;
        if (colData?.status === "success" && Array.isArray(apiColumns)) {
          const nextOrder = buildPositionTableColumnOrderFromPms(apiColumns);
          if (nextOrder?.length) {
            setColumnOrder(nextOrder);
            setColumns(nextOrder);
            columnsToUse = nextOrder;
          } else {
            console.warn(
              "PMS position columns empty after mapping, using default",
            );
          }
        } else {
          console.warn("Failed to fetch column order from PMS, using default");
        }
      }
    } catch (error) {
      console.error("Error fetching column order:", error);
    }

    console.log("✅📉Columns is: ", columnsToUse);
    const url = `${pmsApiBaseUrl}/api/v1/positions`;
    const data = {
      email: userEmail,
      broker_flag: brokerFlag,
    };

    try {
      const response = await fetch(url, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify(data),
      });

      let responseData;
      try {
        responseData = await response.json();
      } catch (parseError) {
        console.warn("Non-JSON response received:", await response.text());
        if (fetchId !== positionFetchIdRef.current) return;
        setHoldings([]);
        setIsLoading(false);
        return;
      }

      if (fetchId !== positionFetchIdRef.current) {
        return;
      }

      const apiPayload = responseData?.data || {};
      const apiStatus = String(apiPayload?.status || "").toLowerCase();

      if (apiStatus === "error") {
        const errMsg =
          apiPayload?.message ||
          apiPayload?.errors?.message ||
          "Failed to load positions.";
        console.error("Positions API error:", apiPayload);
        showErrorToast(errMsg);
        setHoldings([]);
        setIsLoading(false);
        setProgress(100);
        return;
      }

      if (!response.ok || (apiStatus && apiStatus !== "success")) {
        console.warn("Unexpected positions API response:", responseData);
        setHoldings([]);
        setIsLoading(false);
        setProgress(100);
        return;
      }

      const adapter = getPositionAdapter(brokerFlag);
      let positions =
        apiPayload?.positions || responseData?.body?.NetPositionDetail || [];
      positions = adapter.normalizePositions(positions);
      console.log("📦 POSITIONS DATA:", positions);
      console.log("📊 Total positions received:", positions.length);

      // 🔥 Fetch FullName for all positions
      // if (brokerFlag === "5paisa" && positions.length > 0) {
      //   try {
      //     const stocksPayload = positions.map((position) => ({
      //       scripcode: position.ScripCode,
      //       exch: position.Exch?.charAt(0) || "",
      //       exchType: position.ExchType?.charAt(0) || "",
      //       symbol: position.ScripName,
      //     }));

      //     console.log("📤 Sending FullName request for positions...");
      //     console.log("📤 Total positions in payload:", stocksPayload.length);
      //     console.log("📤 First 3 positions:", stocksPayload.slice(0, 3));
      //     console.log(
      //       "📤 Full payload:",
      //       JSON.stringify({ stocks: stocksPayload }, null, 2),
      //     );

      //     const fullNameResponse = await fetch(
      //       "https://beta.infomanav.in/keep/zerodhaAPI/api.php?xAction=fetchPositionPageFullName",
      //       {
      //         method: "POST",
      //         headers: {
      //           "Content-Type": "application/json",
      //           Accept: "application/json",
      //         },
      //         body: JSON.stringify({ stocks: stocksPayload }),
      //       },
      //     );

      //     console.log("📡 Response status:", fullNameResponse.status);
      //     console.log("📡 Response ok:", fullNameResponse.ok);
      //     console.log("📡 Response status text:", fullNameResponse.statusText);
      //     console.log(
      //       "📡 Response headers:",
      //       Object.fromEntries(fullNameResponse.headers),
      //     );

      //     // Get raw text first
      //     const responseText = await fullNameResponse.text();
      //     console.log("📄 Raw response text length:", responseText.length);
      //     console.log(
      //       "📄 Raw response (first 500 chars):",
      //       responseText.substring(0, 500),
      //     );

      //     // Parse JSON
      //     let fullNameResult;
      //     try {
      //       fullNameResult = JSON.parse(responseText);
      //       console.log("✅ Successfully parsed JSON");
      //       console.log("✅ Parsed result:", fullNameResult);
      //     } catch (parseError) {
      //       console.error("❌ JSON parse error:", parseError.message);
      //       console.error("🔍 Full response text:", responseText);
      //       throw new Error(`Invalid JSON response: ${parseError.message}`);
      //     }

      //     // Check if API returned success
      //     if (fullNameResult.status === "success") {
      //       console.log("✅ API returned success status");

      //       if (fullNameResult.data && Array.isArray(fullNameResult.data)) {
      //         console.log(
      //           `✅ Received ${fullNameResult.count} FullName results`,
      //         );
      //         console.log(
      //           "📊 Sample results:",
      //           fullNameResult.data.slice(0, 3),
      //         );

      //         // Merge FullName into positions
      //         positions = positions.map((position, index) => {
      //           const matchedStock = fullNameResult.data.find(
      //             (item) =>
      //               item.scripcode == position.ScripCode &&
      //               item.exch === position.Exch?.charAt(0) &&
      //               item.exchType === position.ExchType?.charAt(0),
      //           );

      //           if (matchedStock) {
      //             console.log(
      //               `✅ [${index}] Matched ${position.ScripName} (${position.ScripCode}): ${matchedStock.fullName}`,
      //             );
      //           } else {
      //             console.warn(
      //               `⚠️ [${index}] No match for ${position.ScripName} (ScripCode: ${position.ScripCode}, Exch: ${position.Exch?.charAt(0)}, ExchType: ${position.ExchType?.charAt(0)})`,
      //             );
      //           }

      //           return {
      //             ...position,
      //             FullName:
      //               matchedStock?.fullName || position.ScripName || "N/A",
      //             name: matchedStock?.fullName || position.ScripName || "N/A",
      //           };
      //         });

      //         console.log(
      //           "✅ FullName data merged successfully into positions",
      //         );
      //         console.log("📦 Sample merged position:", positions[0]);
      //       } else {
      //         console.warn(
      //           "⚠️ API returned success but data is missing or not an array",
      //         );
      //         console.warn("⚠️ Full result:", fullNameResult);

      //         // Add fallback FullName and name
      //         positions = positions.map((position) => ({
      //           ...position,
      //           FullName: position.ScripName || "N/A",
      //           name: position.ScripName || "N/A",
      //         }));
      //       }
      //     } else {
      //       console.warn(
      //         "⚠️ API returned non-success status:",
      //         fullNameResult.status,
      //       );
      //       console.warn("⚠️ API message:", fullNameResult.message);
      //       console.warn("⚠️ Full result:", fullNameResult);

      //       // Add fallback FullName and name
      //       positions = positions.map((position) => ({
      //         ...position,
      //         FullName: position.ScripName || "N/A",
      //         name: position.ScripName || "N/A",
      //       }));
      //     }
      //   } catch (error) {
      //     console.error("❌ Error in FullName fetch operation:", error);
      //     console.error("❌ Error name:", error.name);
      //     console.error("❌ Error message:", error.message);
      //     console.error("❌ Error stack:", error.stack);

      //     // Continue without FullName if API fails - add fallback
      //     console.log("⚠️ Continuing without FullName data...");
      //     positions = positions.map((position) => ({
      //       ...position,
      //       FullName: position.ScripName || "N/A",
      //       name: position.ScripName || "N/A",
      //     }));
      //   }
      // } else {
      //   console.log("ℹ️ No positions to fetch FullName for");
      // }

      console.log("📦 Transformed positions with FullName:", positions);

      // Update livePriceCache
      positions.forEach((position) => {
        const cacheKey =
          position?.ScripCode ||
          position?.scripcode ||
          position?.token ||
          position?.symboltoken;
        if (cacheKey) {
          livePriceCache.current[cacheKey] = Number(position.LTP) || 0;
        }
      });
      console.log("💰 livePriceCache after fetch:", livePriceCache.current);

      setHoldings(positions);

      // 🔹 Call your PHP API to enrich with target/initialPrice
      // try {
      //   console.log("📤 Fetching position targets...");
      //   const enrichResponse = await fetch(
      //     `https://beta.infomanav.in/keep/zerodhaAPI/api.php?xAction=getPositionsWithTargets`,
      //     {
      //       method: "POST",
      //       headers: { "Content-Type": "application/json" },
      //       body: JSON.stringify({
      //         userId: sharedData.ID,
      //         positions: positions,
      //       }),
      //     },
      //   );

      //   const enrichData = await enrichResponse.json();
      //   console.log("✅ Enriched positions:", enrichData);

      //   if (enrichData.status === "success") {
      //     // Ensure enriched positions also have FullName and name
      //     const enrichedPositions = enrichData.positions.map((position) => ({
      //       ...position,
      //       FullName: position.FullName || position.ScripName || "N/A",
      //       name:
      //         position.name || position.FullName || position.ScripName || "N/A",
      //     }));
      //     console.log("✅ Setting enriched positions to state");
      //     setHoldings(enrichedPositions);
      //   } else {
      //     console.warn(
      //       "⚠️ Enrichment API returned non-success, using base positions",
      //     );
      //     setHoldings(positions);
      //   }
      // } catch (err) {
      //   console.error("❌ Error enriching positions:", err);
      //   console.log("⚠️ Using base positions without enrichment");
      //   setHoldings(positions);
      // }

      setTimeout(() => {
        if (fetchId !== positionFetchIdRef.current) return;
        setIsLoading(false);
        console.log("✅ Loading complete");
      }, 1500);

      if (
        brokerFlag === "5paisa" &&
        (apiStatus === "success" || responseData?.head?.status === "0") &&
        positions.length > 0
      ) {
        console.log("🔌 Setting up global WebSocket subscriptions for positions...");
        subscribePositionFeeds(positions);
      } else if (
        brokerFlag === "angelone" &&
        apiStatus === "success" &&
        positions.length > 0
      ) {
        console.log("🔌 Setting up AngelOne WebSocket subscriptions for positions...");
        subscribeAngelPositionFeeds(positions);
      } else {
        console.log(
          "ℹ️ Skipping WebSocket setup (no positions or not success)",
        );
      }

      setProgress(100);
    } catch (error) {
      console.error("❌ Error fetching positions:", error);
      console.error("❌ Error details:", {
        name: error.name,
        message: error.message,
        stack: error.stack,
      });
      if (fetchId !== positionFetchIdRef.current) return;
      setHoldings([]);
      setIsLoading(false);
      setProgress(100);
    }
  };

  const handlePreview = () => setIsPreview(true);
  const handleGoBack = () => setIsPreview(false);

  const SettingBuyModalDetails = (stockData, Type) => {
    setBuySellModalDetails((prevState) => ({
      ...prevState,
      FullName: stockData.FullName || stockData.fullName || "",
      Symbol: stockData.Symbol || stockData.symbol || stockData.name || "",
      Type: Type,
      OrderQty: 1,
      LimitPrice: 0,
      order: stockData, // store complete order object
    }));
  };

  //----------------------------//  Pagination Functions //----------------------------//`

  // Pagination calculations
  const indexOfLastItem = currentPage * pageSize;
  const indexOfFirstItem = indexOfLastItem - pageSize;
  const currentItems = sortedHoldings.slice(
    indexOfFirstItem,
    indexOfLastItem,
  );
  const totalPages = Math.ceil(sortedHoldings.length / pageSize);

  const renderHeaderMenu = useCallback((col) => {
    if (col === "Actions") return null;
    const isOpen = activeHeaderMenu?.column === col;
    const selectedDirection = sortConfig?.column === col ? sortConfig?.direction : null;
    const menu = isOpen ? (
      <div
        style={{
          position: "fixed",
          top: activeHeaderMenu?.top ?? 0,
          left: activeHeaderMenu?.left ?? 0,
          width: 184,
          background: "#ffffff",
          borderRadius: 12,
          border: "1px solid #dde3ea",
          boxShadow: "0 10px 24px rgba(18, 34, 51, 0.18)",
          padding: "7px",
          zIndex: 1200,
        }}
      >
        <button type="button" onClick={() => { setSortConfig({ column: col, direction: "asc" }); setCurrentPage(1); setActiveHeaderMenu(null); }} style={{ width: "100%", border: "none", background: selectedDirection === "asc" ? "#f4f8ff" : "transparent", textAlign: "left", padding: "7px 9px", borderRadius: 8, color: "#44546a", fontWeight: 500, fontSize: 13, cursor: "pointer" }}>
          <span style={{ display: "inline-flex", alignItems: "center", gap: 8 }}><ArrowUp size={14} /><span>Sort Ascending</span></span>
        </button>
        <button type="button" onClick={() => { setSortConfig({ column: col, direction: "desc" }); setCurrentPage(1); setActiveHeaderMenu(null); }} style={{ width: "100%", border: "none", background: selectedDirection === "desc" ? "#f4f8ff" : "transparent", textAlign: "left", padding: "7px 9px", borderRadius: 8, color: "#44546a", fontWeight: 500, fontSize: 13, cursor: "pointer" }}>
          <span style={{ display: "inline-flex", alignItems: "center", gap: 8 }}><ArrowDown size={14} /><span>Sort Descending</span></span>
        </button>
      </div>
    ) : null;
    return (
      <div style={{ position: "absolute", right: col === "Name" ? 4 : 6, top: "50%", transform: "translateY(-50%)" }}>
        <button type="button" onMouseDown={(e) => { e.preventDefault(); e.stopPropagation(); }} onClick={(e) => { e.preventDefault(); e.stopPropagation(); const rect = e.currentTarget.getBoundingClientRect(); const menuWidth = 184; const viewportPadding = 8; const computedLeft = Math.min(Math.max(viewportPadding, rect.right - menuWidth), window.innerWidth - menuWidth - viewportPadding); setActiveHeaderMenu((prev) => prev?.column === col ? null : { column: col, top: rect.bottom + 6, left: computedLeft }); }} style={{ border: "none", background: "transparent", width: 24, height: 24, padding: 0, display: "inline-flex", alignItems: "center", justifyContent: "center", color: "#7f8a99", cursor: "pointer", borderRadius: 6 }}>
          <EllipsisVertical size={14} strokeWidth={2.2} />
        </button>
        {menu && typeof document !== "undefined" ? createPortal(menu, document.body) : null}
      </div>
    );
  }, [activeHeaderMenu, sortConfig]);

  // Handle page change
  const handlePageChange = (pageNumber) => {
    setCurrentPage(pageNumber);
  };

  // Handle page size change
  const handlePageSizeChange = (event) => {
    setPageSize(Number(event.target.value));
    setCurrentPage(1); // Reset to first page when page size changes
  };

  // Generate page numbers to display
  const getPageNumbers = () => {
    const pageNumbers = [];
    const maxPagesToShow = 3;

    if (totalPages <= maxPagesToShow) {
      for (let i = 1; i <= totalPages; i++) {
        pageNumbers.push(i);
      }
    } else {
      if (currentPage <= 2) {
        for (let i = 1; i <= 3; i++) pageNumbers.push(i);
      } else if (currentPage >= totalPages - 1) {
        for (let i = totalPages - 2; i <= totalPages; i++) pageNumbers.push(i);
      } else {
        for (let i = currentPage - 1; i <= currentPage + 1; i++)
          pageNumbers.push(i);
      }
    }

    return pageNumbers;
  };

  //----------------------------//  Buy-Sell Functions //----------------------------//`

  // Placing Order steps:
  // CDSL Record Count Function
  // CDSL Record Count Function with Debug Logging
  async function getCDSLRecordCount(userId) {
    console.log("🔍 getCDSLRecordCount - Starting function");
    console.log("📋 Input userId:", userId);

    const apiUrl =
      "https://beta.infomanav.in/keep/zerodhaAPI/api.php?xAction=getCDSLRecord";
    console.log("🌐 API URL:", apiUrl);

    try {
      console.log("📤 Preparing to send request...");

      const requestBody = { userId: userId };
      console.log("📦 Request body:", JSON.stringify(requestBody));

      const response = await fetch(apiUrl, {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
        },
        body: JSON.stringify(requestBody),
      });

      console.log("📥 Response received");
      console.log("✅ Response status:", response.status);
      console.log("📊 Response statusText:", response.statusText);
      console.log("🔗 Response OK:", response.ok);

      if (!response.ok) {
        console.error("❌ Response not OK - throwing error");
        throw new Error(`Error: ${response.status} - ${response.statusText}`);
      }

      console.log("🔄 Parsing response as JSON...");
      const data = await response.json();
      console.log("📋 Raw response data:", data);

      const count = data.count;
      console.log("🔢 Extracted count:", count);
      console.log("✅ getCDSLRecordCount - Function completed successfully");

      return count;
    } catch (error) {
      console.error("💥 Error in getCDSLRecordCount:");
      console.error("❌ Error message:", error.message);
      console.error("📊 Full error object:", error);
      console.log("🔄 Returning default value: 0");

      // Return 0 as default if error occurs
      return 0;
    }
  }

  const fetchDataPortfolio = (watchlist, transactionSide, status) => {
    console.log("🚀 fetchDataPortfolio - Starting function");
    console.log("📊 Input parameters:");
    console.log("   - watchlist:", watchlist);
    console.log("   - transactionSide:", transactionSide);
    console.log("   - status:", status);

    const Exch = watchlist.Exch;
    console.log("🏛️ Exchange (Exch):", Exch);

    const ExchType = Exch === "M" ? "D" : "C";
    console.log(
      "📈 Exchange Type (ExchType):",
      ExchType,
      `(${Exch === "M" ? "Derivative" : "Cash"})`,
    );

    const Name = watchlist.FullName;
    console.log("🏢 Company Name:", Name);

    let Scripcode = "";

    if (status === "Portfolio") {
      // ✨ Use Number conversion for ScripCode with fallback
      Scripcode = Number(watchlist.ScripCode) || Number(watchlist.scripcode);
      console.log("📋 Portfolio mode - Using pre-calculated ScripCode:");
      console.log(
        `   - Exchange is ${watchlist.Exch}, ScripCode: ${Scripcode}`,
      );
      console.log(
        `   - NseCode: ${watchlist.NseCode}, BseCode: ${watchlist.BseCode}`,
      );
      console.log(`   - Selected Scripcode: ${Scripcode}`);
    } else {
      Scripcode = watchlist.Scripcode;
      console.log("📝 Watchlist mode - Using direct Scripcode:", Scripcode);
    }

    let watchlist_price = "0";
    let min_qty = "0";

    // ✅ Get values from modal state instead of DOM
    watchlist_price =
      orderType === "Market Order"
        ? "0"
        : buySellModalDetails.LimitPrice.toString();
    min_qty = buySellModalDetails.OrderQty.toString();

    console.log("💰 Price & Quantity from modal:");
    console.log("   - Execution (Market/Limit):", orderType);
    console.log("   - Price (watchlist_price):", watchlist_price);
    console.log("   - Quantity (min_qty):", min_qty);
    console.log("   - buySellModalDetails:", buySellModalDetails);

    const userId = sharedData.ID;
    console.log("👤 User ID:", userId);
    console.log("📱 sharedData object:", sharedData);

    const order_type = transactionSide;
    console.log("📊 Final order_type (Buy/Sell):", order_type);

    // Create a JavaScript object with the request data
    const requestData = {
      ExchType,
      order_type,
      Exch,
      Name,
      Scripcode,
      watchlist_price,
      min_qty,
      userId,
    };

    console.log("📦 Complete request data object:");
    console.log(JSON.stringify(requestData, null, 2));

    // ✅ RESTORED: CDSL validation for Sell orders
    if (transactionSide === "Sell") {
      console.log("🛑 SELL order detected - checking CDSL validation");
      getCDSLRecordCount(userId)
        .then((count) => {
          console.log("✅ CDSL validation completed");
          console.log("🔢 CDSL Record Count:", count);

          if (count === 0) {
            console.error(
              "❌ CDSL Record Count is 0 - cannot place sell order",
            );
            console.log("🚫 Sell order blocked - CDSL setup required");
            // TODO: Show error modal - user needs to complete CDSL setup
            // document.getElementById("modalClick").click();
          } else {
            console.log(
              "✅ CDSL validation passed - proceeding with sell order",
            );
            executeBuySellOrderPortfolio(
              requestData,
              watchlist,
              transactionSide,
              status,
            );
          }
        })
        .catch((error) => {
          console.error("💥 Error getting CDSL count:", error);
          console.log(
            "⚠️ CDSL validation failed - proceeding with order anyway",
          );
          // Proceed with order anyway or show error
          executeBuySellOrderPortfolio(
            requestData,
            watchlist,
            transactionSide,
            status,
          );
        });
    } else if (transactionSide === "Buy") {
      console.log("💚 BUY order detected - no CDSL validation needed");
      console.log("🚀 Proceeding directly to order execution");
      executeBuySellOrderPortfolio(
        requestData,
        watchlist,
        transactionSide,
        status,
      );
    } else {
      console.error("❓ Unknown transaction side:", transactionSide);
    }

    console.log("🏁 fetchDataPortfolio - Function end");
  };

  // Function to handle the checkSell value
  // const executeBuySellOrderPortfolio = (requestData, watchlist, orderType, status) => {
  //   console.log("🔧 executeBuySellOrderPortfolio - Starting function");
  //   console.log("📥 Input parameters:");
  //   console.log("   - requestData:", requestData);
  //   console.log("   - watchlist:", watchlist);
  //   console.log("   - orderType:", orderType);
  //   console.log("   - status:", status);

  //   const apiUrl = "https://beta.infomanav.in/keep/zerodhaAPI/api.php?xAction=addFivePaisaOrder";
  //   console.log("🌐 API URL:", apiUrl);
  //   console.log("📤 Preparing to send order to database...");

  //   // Send a POST request using fetch
  //   fetch(apiUrl, {
  //     method: "POST",
  //     headers: {
  //       "Content-Type": "application/json",
  //     },
  //     body: JSON.stringify(requestData),
  //   })
  //     .then((response) => {
  //       console.log("📥 Database response received");
  //       console.log("✅ Response status:", response.status);
  //       console.log("📊 Response statusText:", response.statusText);
  //       console.log("🔗 Response OK:", response.ok);

  //       if (!response.ok) {
  //         console.error("❌ Database response not OK");
  //         throw new Error("Network response was not ok");
  //       }

  //       console.log("🔄 Parsing database response as JSON...");
  //       return response.json();
  //     })
  //     .then((data) => {
  //       console.log("📋 Database response data:", data);

  //       // Handle the response data here
  //       if (data.message === "No data found in table") {
  //         console.log("⚠️ No data found in order table");
  //         console.log("🛑 Order execution stopped");
  //       } else {
  //         console.log("✅ Database order saved successfully");
  //         console.log("🚀 Proceeding to place order with 5paisa API");
  //         clickPlaceOrderPortfolio(watchlist, orderType, status);

  //         console.log("🔄 Starting holdings refresh...");

  //         const holdingsRefreshData = {
  //           AccessToken: sharedData.AccessToken,
  //           ClientCode: sharedData.ClientCode,
  //           api_key: sharedData.api_key,
  //           userId: sharedData.ID,
  //         };

  //         console.log("📦 Holdings refresh request data:", holdingsRefreshData);

  //         // ✅ Refresh holdings in DB
  //         fetch("https://beta.infomanav.in/keep/zerodhaAPI/api.php?xAction=getHoldingData", {
  //           method: "POST",
  //           headers: { "Content-Type": "application/json" },
  //           body: JSON.stringify(holdingsRefreshData),
  //         })
  //           .then((res) => {
  //             console.log("📥 Holdings refresh response received");
  //             console.log("✅ Holdings refresh status:", res.status);
  //             console.log("🔗 Holdings refresh OK:", res.ok);
  //             return res.json();
  //           })
  //           .then((holdingsData) => {
  //             console.log("✅ Holdings refreshed successfully");
  //             console.log("📊 Updated holdings data:", holdingsData);
  //           })
  //           .catch((err) => {
  //             console.error("💥 Error refreshing holdings:");
  //             console.error("❌ Holdings refresh error:", err);
  //           });
  //       }
  //     })
  //     .catch((error) => {
  //       console.error("💥 Database operation failed:");
  //       console.error("❌ Fetch error message:", error.message);
  //       console.error("📊 Full error object:", error);
  //       console.log("🛑 Order execution terminated due to database error");
  //     });

  //   console.log("🏁 executeBuySellOrderPortfolio - Function end (async operations continue)");
  // };

  const executeBuySellOrderPortfolio = (
    requestData,
    watchlist,
    transactionSide,
    status,
  ) => {
    console.log("🔧 executeBuySellOrderPortfolio - Starting function");
    console.log("📥 Input parameters:");
    console.log("   - requestData:", requestData);
    console.log("   - watchlist:", watchlist);
    console.log("   - transactionSide:", transactionSide);
    console.log("   - status:", status);

    // ✅ REMOVED: Database call - now happens AFTER order placement
    console.log("🚀 Proceeding to PMS place_order");
    clickPlaceOrderPortfolio(requestData, watchlist, transactionSide, status);

    console.log("🏁 executeBuySellOrderPortfolio - Function end");
  };

  const clickPlaceOrderPortfolio = (
    requestData,
    watchlist,
    transactionSide,
    status,
  ) => {
    const executionType = orderType;

    console.log("🎯 clickPlaceOrderPortfolio — PMS /api/v1/place_order");
    console.log(
      "   transactionSide:",
      transactionSide,
      "executionType:",
      executionType,
    );

    placeOrderViaPms({
      pmsApiBaseUrl,
      sharedData,
      watchlist,
      listStatus: status,
      transactionSide,
      executionType,
      qty: buySellModalDetails.OrderQty,
      limitPrice: buySellModalDetails.LimitPrice,
    }).then((result) => {
      if (result.error) {
        console.error("💥 place_order:", result.error);
        showErrorToast("Failed to place order. Please try again.");
        setTimeout(() => navigate("/Order"), 1500);
        return;
      }

      const data = result.data;
      console.log("📋 place_order response:", JSON.stringify(data, null, 2));

      const normalized = normalizePlaceOrderResponse(data);

      if (!result.ok) {
        const msg = normalized.message || "Failed to place order";
        showErrorToast(msg);
        setTimeout(() => navigate("/Order"), 1500);
        return;
      }

      const message = normalized.message || "";
      const rmsResponseCode = normalized.rmsResponseCode;
      const brokerOrderID = normalized.brokerOrderID;
      const exchangeOrderID = normalized.exchangeOrderID;

      if (
        isFivePaisaBroker(sharedData) &&
        (rmsResponseCode === -162 ||
          message.toLowerCase().includes("sell authorization not received") ||
          message.toLowerCase().includes("authorization not received"))
      ) {
        showErrorToast("CDSL authorization required to place sell orders");
        if (transactionSide === "Buy") {
          document.getElementById("buyModalClick")?.click();
        } else if (transactionSide === "Sell") {
          document.getElementById("sellModalClick")?.click();
        }
        setTimeout(() => {
          document.getElementById("openCDSLModalBtn")?.click();
        }, 500);
        return;
      }

      if (normalized.headStatus !== "0") {
        const rejectionMsg = String(message || "");
        const isGrowwTpinError =
          !isFivePaisaBroker(sharedData) &&
          String(transactionSide || "").toLowerCase() === "sell" &&
          rejectionMsg
            .toLowerCase()
            .includes("verify your holdings first via tpin");

        if (isGrowwTpinError) {
          setGrowwTpinErrorText(
            rejectionMsg ||
            "Verify your holdings first via TPIN from Groww Dashboard.",
          );
          if (transactionSide === "Buy") {
            document.getElementById("buyModalClick")?.click();
          } else if (transactionSide === "Sell") {
            document.getElementById("sellModalClick")?.click();
          }
          setTimeout(() => {
            document.getElementById("openGrowwTPINModalBtn")?.click();
          }, 500);
          return;
        }

        showErrorToast(rejectionMsg || "Failed to place order");
        setTimeout(() => navigate("/Order"), 1500);
        return;
      }

      runPlaceOrderAcceptedFlow({
        transactionSide,
        tr,
        showSuccessToast,
        navigate,
      });
    });

    console.log(
      "🏁 clickPlaceOrderPortfolio - Function end (async PMS call continues)",
    );
  };

  // const refreshHoldings = () => {
  //   console.log("🔄 Starting holdings refresh...");

  //   const holdingsRefreshData = {
  //     AccessToken: sharedData.AccessToken,
  //     ClientCode: sharedData.ClientCode,
  //     api_key: sharedData.api_key,
  //     userId: sharedData.ID,
  //   };

  //   console.log("📦 Holdings refresh request data:", holdingsRefreshData);

  //   fetch("https://beta.infomanav.in/keep/zerodhaAPI/api.php?xAction=getHoldingData", {
  //     method: "POST",
  //     headers: { "Content-Type": "application/json" },
  //     body: JSON.stringify(holdingsRefreshData),
  //   })
  //     .then((res) => {
  //       console.log("📥 Holdings refresh response received");
  //       console.log("✅ Holdings refresh status:", res.status);
  //       return res.json();
  //     })
  //     .then((holdingsData) => {
  //       console.log("✅ Holdings refreshed successfully");
  //       console.log("📊 Updated holdings data:", holdingsData);
  //     })
  //     .catch((err) => {
  //       console.error("💥 Error refreshing holdings:");
  //       console.error("❌ Holdings refresh error:", err);
  //     });
  // };

  const refreshHoldings = () => {
    console.log("🔄 Starting holdings refresh...");
  };

  const updateMargin = () => {
    console.log("💰 updateMargin - Starting margin update");

    // Get sharedData from localStorage
    const saved = JSON.parse(localStorage.getItem("sharedData"));

    if (!saved || !saved.accessToken || !saved.api_key || !saved.ClientCode) {
      console.error("❌ Missing required data for margin update");
      return;
    }

    const apiUrl =
      "https://Openapi.5paisa.com/VendorsAPI/Service1.svc/V4/Margin";
    console.log("🌐 Margin API URL:", apiUrl);

    const requestBody = {
      head: {
        key: saved.api_key,
      },
      body: {
        ClientCode: saved.ClientCode,
      },
    };

    console.log("📦 Margin request body:", requestBody);

    const requestHeaders = {
      Authorization: `Bearer ${saved.accessToken}`,
      "Content-Type": "application/json",
    };

    console.log("📤 Sending margin update request...");

    fetch(apiUrl, {
      method: "POST",
      headers: requestHeaders,
      body: JSON.stringify(requestBody),
    })
      .then((response) => {
        console.log("📥 Margin API response received");
        console.log("✅ Response status:", response.status);

        if (!response.ok) {
          console.error("❌ Margin API response not OK");
          throw new Error("Margin API response was not ok");
        }

        return response.json();
      })
      .then((data) => {
        console.log("📋 Margin API Response Data:");
        console.log(JSON.stringify(data, null, 2));

        // Extract NetAvailableMargin from response
        const equityMargin = data.body?.EquityMargin?.[0];

        if (equityMargin && equityMargin.NetAvailableMargin !== undefined) {
          const netAvailableMargin = equityMargin.NetAvailableMargin;
          console.log("💵 NetAvailableMargin:", netAvailableMargin);

          // Update sharedData in localStorage
          const currentSharedData = JSON.parse(
            localStorage.getItem("sharedData"),
          );
          currentSharedData.margin = netAvailableMargin;

          localStorage.setItem("sharedData", JSON.stringify(currentSharedData));
          console.log("✅ Margin updated in localStorage:", netAvailableMargin);

          // 🆕 Optional: Update state if you have margin in component state
          // setSharedData(currentSharedData); // Uncomment if you use state

          // Show success toast
          console.log("💰 Available Margin: ₹" + netAvailableMargin.toFixed(2));
        } else {
          console.error("❌ NetAvailableMargin not found in response");
          console.error("📊 EquityMargin data:", equityMargin);
        }
      })
      .catch((error) => {
        console.error("💥 Margin API call failed:");
        console.error("❌ Error type:", error.name);
        console.error("❌ Error message:", error.message);
        console.error("📊 Full error object:", error);
      });

    console.log("🏁 updateMargin - Function end (async call continues)");
  };

  const getPriceForCalculation = () => {
    if (orderType === "Market Order") {
      // Use live price for Market orders
      const order = buySellModalDetails.order || {};
      const rawScripCode = order?.ScripCode ?? order?.scripcode;
      const numericScripCode =
        rawScripCode !== undefined && rawScripCode !== null && rawScripCode !== ""
          ? Number(rawScripCode)
          : NaN;
      const cacheKey = Number.isFinite(numericScripCode) && numericScripCode > 0
        ? numericScripCode
        : String(rawScripCode || "").trim();
      const livePrice = cacheKey ? livePriceCache.current[cacheKey] : undefined;
      return (
        livePrice ||
        order?.CurrentPrice ||
        order?.LTP ||
        order?.initialPrice ||
        order?.net_price ||
        0
      );
    } else {
      // Use limit price for Limit orders
      return buySellModalDetails.LimitPrice || 0;
    }
  };

  //----------------------------// End Buy-Sell Functions //----------------------------//`

  //----------------------------//  Market Depth Functions //----------------------------//`
  // Function to subscribe to MarketDepth for a given stock
  // 🔥 Subscribe to Market Depth (Order Book) for a stock
  // const subscribeToMarketDepth = (holding, clientCode) => {
  //   const currentWs = wsRef.current;

  //   if (!currentWs || currentWs.readyState !== WebSocket.OPEN) {
  //     console.warn("📉WebSocket not connected for depth, readyState:", currentWs?.readyState);
  //     return;
  //   }

  //   const depthMessage = {
  //     Method: "MarketDepthService",
  //     Operation: "Subscribe",
  //     ClientCode: clientCode,
  //     MarketFeedData: [
  //       {
  //         Exch: holding.Exch,
  //         ExchType: holding.ExchType,
  //         ScripCode: holding.ScripCode
  //       }
  //     ]
  //   };

  //   try {
  //     currentWs.send(JSON.stringify(depthMessage));
  //     console.log(`📉📊 Subscribed to Market Depth for ${holding.ScripName} (${holding.ScripCode})`);
  //   } catch (error) {
  //     console.error("❌ Error subscribing to Market Depth:", error);
  //   }
  // };

  const subscribeToMarketDepth = (holding) => {
    if (!holding) return;

    if (activeBrokerFlag === "5paisa") {
      fivePaisaMarketDepthStore.openDepth(holding);
      return;
    }

    if (activeBrokerFlag === "angelone") {
      const userId =
        Number(localStorage.getItem("id")) || Number(sharedData?.ID) || 0;
      angelOneMarketDepthService.openDepth(holding, {
        pmsApiBaseUrl,
        userId,
      });
    }
  };

  const unsubscribeFromMarketDepth = () => {
    if (activeBrokerFlag === "5paisa") {
      fivePaisaMarketDepthStore.closeDepth();
      return;
    }

    if (activeBrokerFlag === "angelone") {
      angelOneMarketDepthService.closeDepth();
    }
  };

  // Function to run when modal closes

  const handleModalClose = () => {
    console.log("📉📉📉 MarketDepth modal closed!");

    if (currentMDModalRef.current) {
      console.log("📉 Unsubscribing to:", currentMDModalRef.current);
      unsubscribeFromMarketDepth();
      console.log("📉 Unsubscribed");

      // Clear the ref so it doesn't hold stale data
      currentMDModalRef.current = null;
      setMarketDepth([]);
    }
  };

  
  const closeMarketDepthPopover = () => {
    unsubscribeFromMarketDepth();
    setcurrentMDModal(null);
    setMarketDepth([]);
    setShowMarketDepth(false);
  };

  //----------------------------// End market Depth Functions //----------------------------//`

  //----------------------------// Drag-N-Drop Functions //----------------------------//
  // Save column order to PMS (module_name must stay lowercase: "position")
  const saveColumnOrder = async (newOrder) => {
    try {
      const userEmail =
        sharedData?.Email_ID ||
        sharedData?.email ||
        localStorage.getItem("email") ||
        "";
      if (!userEmail) {
        console.warn("saveColumnOrder: missing email, skipping PMS reorder");
        return;
      }

      const brokerFlag = toPmsBrokerFlag(activeBrokerFlag);
      const columnsPayload = [];
      let sortOrder = 1;
      for (const label of newOrder) {
        if (label === "Actions") continue;
        const columnKey = POSITION_TABLE_COL_TO_PMS_COLUMN_KEY[label];
        if (!columnKey) {
          console.warn(
            "saveColumnOrder: no PMS column_key for table column:",
            label,
          );
          continue;
        }
        columnsPayload.push({ column_key: columnKey, sort_order: sortOrder });
        sortOrder += 1;
      }
      if (columnsPayload.length === 0) return;

      const response = await fetch(`${pmsApiBaseUrl}/api/v1/columns/order`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({
          broker_flag: brokerFlag,
          module_name: "position",
          user_id: Number(sharedData?.ID || localStorage.getItem("id") || 0),
          email: userEmail,
          columns: columnsPayload,
        }),
      });
      const result = await response.json();
      console.log("Position column reorder (PMS):", result);
    } catch (err) {
      console.error("Error saving column order:", err);
    }
  };

  // Drag Start
  const handleDragStart = (e, columnKey) => {
    setDraggedColumn(columnKey);

    const dragPreview = document.createElement("div");
    dragPreview.style.position = "absolute";
    dragPreview.style.top = "-9999px";
    dragPreview.style.padding = "6px 16px";
    dragPreview.style.background = "#C9E0FF";
    dragPreview.style.border = "1px solid #99bde0";
    dragPreview.style.borderRadius = "4px";
    dragPreview.style.color = "#000";
    dragPreview.style.fontSize = "16px";
    dragPreview.style.fontWeight = "bold";
    dragPreview.style.boxShadow = "0 2px 6px rgba(0, 0, 0, 0.15)";
    dragPreview.textContent = columnKey;

    document.body.appendChild(dragPreview);
    e.dataTransfer.setDragImage(dragPreview, 0, 0);

    // Cleanup
    setTimeout(() => {
      document.body.removeChild(dragPreview);
    }, 0);
  };

  // Drag Over
  const handleDragOver = (e) => {
    e.preventDefault(); // Allow drop
  };

  const handleDrop = (e, targetColumnKey) => {
    e.preventDefault();

    const draggedIndex = columnOrder.indexOf(draggedColumn);
    const targetIndex = columnOrder.indexOf(targetColumnKey);

    if (
      draggedIndex === -1 ||
      targetIndex === -1 ||
      draggedIndex === targetIndex
    ) {
      setDraggedColumn(null);
      return;
    }

    const tableContainer = document.querySelector(".table")?.closest("div");
    if (tableContainer) tableContainer.style.overflowY = "hidden";

    const newColumnOrder = [...columnOrder];
    const [movedColumn] = newColumnOrder.splice(draggedIndex, 1);
    newColumnOrder.splice(targetIndex, 0, movedColumn);

    setColumnOrder(newColumnOrder);
    setDraggedColumn(null);
    saveColumnOrder(newColumnOrder);

    // Animation wrapped in requestAnimationFrame to prevent ResizeObserver loops
    requestAnimationFrame(() => {
      const cells = document.querySelectorAll(
        `[data-column-id="${movedColumn}"]`,
      );

      // Start animation
      cells.forEach((cell) => cell.classList.add("column-drop-animate"));

      // Remove animation after timeout
      setTimeout(() => {
        cells.forEach((cell) => cell.classList.remove("column-drop-animate"));

        // Temporary highlight of table
        const table = document.querySelector("table");
        table?.classList.add("table-highlight-column");
        setTimeout(() => {
          table?.classList.remove("table-highlight-column");
          if (tableContainer) tableContainer.style.overflowY = "";
        }, 600);
      }, 850);
    });
  };

  // Drag End
  const handleDragEnd = () => {
    setDraggedColumn(null);
  };

  //----------------------------// End Drag-N-Drop Functions //----------------------------//

  const openSetTargetModal = (holding, fallbackCurrentPrice) => {
    const scripCode = holding?.ScripCode || holding?.scripcode;
    const livePrice = scripCode ? livePriceCache.current?.[scripCode] : undefined;
    const previousClose = scripCode
      ? livePCloseCache.current?.[scripCode]
      : undefined;
    const resolvedCurrentPrice =
      livePrice ?? fallbackCurrentPrice ?? parseFloat(holding?.LTP) ?? 0;

    setTargetDetails({
      ...holding,
      initialPrice: resolvedCurrentPrice,
      currentPrice: resolvedCurrentPrice,
      modalCurrentPrice: resolvedCurrentPrice,
      modalPreviousClose:
        previousClose ??
        holding?.["Current Price"] ??
        holding?.LTP ??
        fallbackCurrentPrice ??
        0,
    });

    setTimeout(() => {
      const modalElement = document.getElementById("SetTargetModal");
      if (!modalElement) return;

      const modal = Modal.getOrCreateInstance(modalElement);
      modal.show();
    }, 0);
  };

  return (
    <>
      {/* <Header /> */}
      <Header tabName={tabName} />
      <div className="wrapper__page pb-5">
        <Sidebar />

        <div className="content_col">
          <h1 className="dashboard-page-title">{tr("position")}</h1>
          {/* <div className="tile__card__row">
                        <div className="tile__card__col">
                            <div className="tile__card__icon">
                                <i className="icon-investment-amount"></i>
                            </div>

                            <div className="tile__card__text">
                                <h6>Total Invested Amount</h6>

                                <h5>
                                    <span>₹</span>
                                    {holdings.reduce((total, holding) => {
                                        return total + (parseFloat(holding.AvgRate) * parseInt(holding.DPQty));
                                    }, 0).toLocaleString('en-IN', {
                                        minimumFractionDigits: 2,
                                        maximumFractionDigits: 2
                                    })}
                                </h5>
                            </div>
                        </div>

                        <div className="tile__card__col">
                            <div className="tile__card__icon">
                                <i className="icon-current-value"></i>
                            </div>



                           
<div className="tile__card__text">
  <h6>Today's P/L(%)</h6>
  <h5
    className={(() => {
      const todaysPL = holdings.reduce((total, holding) => {
        const scripCode = holding.Exch === "N" ? holding.NseCode : holding.BseCode;
        const livePrice = livePriceCache.current[scripCode];
        const pClose = livePCloseCache.current[scripCode];

        const currentPrice = livePrice || holding.CurrentPrice;
        const previousClose = pClose || holding.CurrentPrice;

        const dailyPL =
          (parseFloat(currentPrice) - parseFloat(previousClose)) * holding.DPQty;

        return total + dailyPL;
      }, 0);

      return todaysPL >= 0 ? "text_success_color" : "text_danger_color";
    })()}
  >
    <span>₹</span>
    {(() => {
      const results = holdings.reduce(
        (acc, holding) => {
          const scripCode = holding.Exch === "N" ? holding.NseCode : holding.BseCode;
          const livePrice = livePriceCache.current[scripCode];
          const pClose = livePCloseCache.current[scripCode];

          const currentPrice = livePrice || holding.CurrentPrice;
          const previousClose = pClose || holding.CurrentPrice;

          const dailyPL =
            (parseFloat(currentPrice) - parseFloat(previousClose)) * holding.DPQty;
          const previousDayValue = parseFloat(previousClose) * holding.DPQty;

          acc.totalPL += dailyPL;
          acc.totalPreviousDayValue += previousDayValue;

          return acc;
        },
        { totalPL: 0, totalPreviousDayValue: 0 }
      );

      if (!results.totalPreviousDayValue || isNaN(results.totalPreviousDayValue)) {
        return "0.00 (0.00%)";
      }

      const todaysPLPercent = (
        (results.totalPL / results.totalPreviousDayValue) *
        100
      ).toFixed(2);

      return (
        <>
          {Math.abs(results.totalPL).toLocaleString("en-IN", {
            minimumFractionDigits: 2,
            maximumFractionDigits: 2,
          })} 
          <small>
            ({results.totalPL >= 0 ? "+" : ""}{todaysPLPercent}%)
          </small> 
          <i className={`icon-arrow-${results.totalPL >= 0 ? "up" : "down"}`}></i>
        </>
      );
    })()}
  </h5>
</div>



                        </div>

                        <div className="tile__card__col">
                            <div className="tile__card__icon">
                                <i className="icon-investment-amount"></i>
                            </div>






<div className="tile__card__text">
  <h6>Unrealized P/L(%)</h6>
  <h5 
    className={(() => {
      const investedValue = holdings.reduce((total, holding) => {
        return total + (parseFloat(holding.DPQty) * parseFloat(holding.AvgRate));
      }, 0);

      const currentValue = holdings.reduce((total, holding) => {
        const scripCode = holding.Exch === "N" ? holding.NseCode : holding.BseCode;
        const livePrice = livePriceCache.current[scripCode];
        const currentPrice = livePrice || holding.CurrentPrice;
        return total + (parseFloat(holding.DPQty) * parseFloat(currentPrice));
      }, 0);

      const unrealizedPL = currentValue - investedValue;
      return unrealizedPL >= 0 ? "text_success_color" : "text_danger_color";
    })()}
  >
    <span>₹</span>
    {(() => {
      const investedValue = holdings.reduce((total, holding) => {
        return total + (parseFloat(holding.DPQty) * parseFloat(holding.AvgRate));
      }, 0);

      const currentValue = holdings.reduce((total, holding) => {
        const scripCode = holding.Exch === "N" ? holding.NseCode : holding.BseCode;
        const livePrice = livePriceCache.current[scripCode];
        const currentPrice = livePrice || holding.CurrentPrice;
        return total + (parseFloat(holding.DPQty) * parseFloat(currentPrice));
      }, 0);

      const unrealizedPL = currentValue - investedValue;

      // Handle 0 or missing investedValue
      if (!investedValue || isNaN(investedValue)) {
        return "0.00 (0.00%)";
      }

      const unrealizedPercent = ((unrealizedPL / investedValue) * 100).toFixed(2);

      return (
        <>
          {Math.abs(unrealizedPL).toLocaleString("en-IN", {
            minimumFractionDigits: 2,
            maximumFractionDigits: 2,
          })} 
          <small>
            ({unrealizedPL >= 0 ? "+" : ""}{unrealizedPercent}%)
          </small> 
          <i className={`icon-arrow-${unrealizedPL >= 0 ? "up" : "down"}`}></i>
        </>
      );
    })()}
  </h5>
</div>



                           
                           </div>




                        <div className="tile__card__col">
                            <div className="tile__card__icon">
                                <i className="icon-current-value"></i> 
                            </div>

                            <div className="tile__card__text">
                                <h6>Margin</h6>
                                <h5 className="text_success_color"><span>₹</span>{sharedData.margin}</h5>
                            </div>
                        </div>
                    </div> */}

          <>
            {/* Table */}
            <div className="table_main">
              {/* <div className="table_title_with_view_all_row">
                <h5>{tr("position")}
                </h5>
              </div> */}
              <div className="table-responsive db-position-targets-wrap">
                {isLoading && (
                  <Placeholder.Grid
                    rows={10}
                    columns={5}
                    active
                    style={{ paddingLeft: 20, paddingRight: 20, paddingTop: 12 }}
                  />
                )}

                {!isLoading && (
                  <>
                    <style>{`
    /* Position targets sticky thead with horizontal scroll support */
    .db-position-targets-wrap{
      overflow-x: auto !important;
      overflow-y: auto !important;
      max-height: calc(100vh - 280px);
      position: relative;
    }
    .db-position-targets-table{
      border-collapse: separate;
      border-spacing: 0;
      width: 100%;
      min-width: max-content;
    }
    .db-position-targets-table thead{
      position: sticky;
      top: 0;
      z-index: 50;
    }
      .db-position-targets-table thead tr{
      position: relative;
        background: #f8fbff !important;
    }
    .db-position-targets-table thead th{
      position: -webkit-sticky !important;
      position: sticky !important;
      top: 0 !important;
      z-index: 55 !important;
        background: #f8fbff !important;
    }
    .db-position-targets-table thead th[data-column-id="Actions"]{
      z-index: 60 !important;
      background: #fff !important;
      right: 0 !important;
    }
    .db-position-targets-table tbody td.action-cell{
      position: sticky !important;
      right: 0 !important;
      z-index: 35 !important;
      background: #fff !important;
    }
    /* Target % driven gradient highlight (scoped to Position table) */
    .table_main tr.position-target-gradient-row{
      background: linear-gradient(
        90deg,
        rgba(var(--target-bg-rgb), 1) 0%,
        rgba(var(--target-bg-rgb), 0) 100%
      ) !important;
    }
    .table_main tr.position-target-gradient-row td,
    .table_main tr.position-target-gradient-row:hover td{
      background: transparent !important;
    }

    /* Premium action button hover states (scoped to this page via button classes) */
    .wl-premium-action-btn{
      transition: all 0.2s ease;
    }
    .wl-premium-action-btn:hover{
      transform: translateY(-1px);
    }
    .wl-premium-buy:hover{
      background: #C8F8E8 !important;
      border-color: transparent !important;
      color: #1DAA7F !important;
      box-shadow: 0 4px 10px rgba(35,183,137,0.16) !important;
    }
    .wl-premium-sell:hover{
      background: #F8D3D3 !important;
      border-color: transparent !important;
      color: #BD4545 !important;
      box-shadow: 0 4px 10px rgba(201,74,74,0.14) !important;
    }
    .wl-premium-depth:hover{
      background: #F3F7FF !important;
      box-shadow: 0 5px 14px rgba(20,38,71,0.20) !important;
    }
    .wl-premium-action-btn:disabled{
      transform: none !important;
      box-shadow: none !important;
    }
  `}</style>
                    <table
                      className="table db-position-targets-table"
                      style={{ backgroundColor: "#F8FBFF" }}
                    >
                      <thead>
                        <tr>
                          {columnOrder.map((col) => (
                            <th
                              key={col}
                              draggable="true"
                              onDragStart={(e) => handleDragStart(e, col)}
                              onDragOver={(e) => handleDragOver(e)}
                              onDrop={(e) => handleDrop(e, col)}
                              onDragEnd={handleDragEnd}
                              data-column-id={col}
                              style={{
                                transition: "all 0.3s ease",
                                transform:
                                  draggedColumn === col
                                    ? "scale(1.03)"
                                    : "scale(1)",
                                boxShadow:
                                  draggedColumn === col
                                    ? "0 0 10px #C9E0FF"
                                    : "none",
                                cursor: "grab",
                                textAlign:
                                  col === "Name"
                                    ? "left"
                                    : [
                                      "ProfitPct",
                                      "Volume",
                                      "Exchange",
                                    ].includes(col)
                                      ? "center"
                                      : "center",
                                minWidth: col === "Name" ? "220px" : undefined,
                                maxWidth: col === "Name" ? "220px" : undefined,
                                width: col === "Name" ? "220px" : undefined,
                                paddingLeft: col === "Actions" ? 0 : undefined,
                                paddingRight: col === "Actions" ? 0 : undefined,
                                display: [
                                  "TodaysLow",
                                  "Buy Quantity",
                                  "Target %",
                                  "Avg Buy Price",
                                  "Buy Value",
                                ].includes(col)
                                  ? "none"
                                  : undefined,
                                position: "sticky",
                                top: 0,
                                right: col === "Actions" ? 0 : undefined,
                                zIndex: col === "Actions" ? 60 : 55,
                                background: "#f8fbff",
                                paddingRight: col === "Actions" ? 0 : 28,
                              }}
                            >
                              {renderHeaderMenu(col)}
                              {col === "Name" ? (
                                <div className="table_head_search">
                                  <div style={{ padding: "2px", width: 220 }}>
                                    <button
                                      style={{
                                        padding: "2px 0 2px 5px",
                                        borderRight: "1px solid #e4e4e4ff",
                                      }}
                                      onClick={() =>
                                        searchInputRef.current?.focus()
                                      }
                                    >
                                      <i
                                        className="icon-search"
                                        style={{ fontSize: "14px" }}
                                      />
                                    </button>
                                    {/* <input type="text" placeholder="Name" style={{ width: 90 }} /> */}
                                    {/* Name  */}
                                    <input
                                      type="text"
                                      placeholder={tr("nameSearchHere")}
                                      style={{
                                        paddingLeft: "10px",
                                        marginLeft: "5px",
                                        maxWidth: "150px",
                                        width: "100%",
                                        boxSizing: "border-box",
                                      }}
                                      value={searchQuery}
                                      onChange={(e) =>
                                        setSearchQuery(e.target.value)
                                      }
                                      ref={searchInputRef}
                                    />
                                  </div>
                                  {/* <button><i className="icon-settings" /></button> */}
                                </div>
                              ) : col === "Actions" ? (
                                <></>
                              ) : //   : col === "TodaysHigh" ? (
                                //     <Whisper
                                //       placement="top"
                                //       trigger="hover"
                                //       speaker={
                                //         <Tooltip className="common__tooltip" style={{ maxWidth: '400px' }}>
                                //           Today's High:<br />
                                //           - The highest price at which this futures contract has traded during the current trading session.
                                //         </Tooltip>
                                //       }
                                //     >
                                //       <>
                                //         Today's High
                                //       </>
                                //     </Whisper>
                                //   )

                                //   : col === "TodaysLow" ? (
                                //   <Whisper
                                //     placement="top"
                                //     trigger="hover"
                                //     speaker={
                                //       <Tooltip className="common__tooltip" style={{ maxWidth: '400px' }}>
                                //         Today's Low:<br />
                                //         - The lowest price at which this futures contract has traded during the current trading session.
                                //       </Tooltip>
                                //     }
                                //   >
                                //     <>
                                //       Today's Low
                                //     </>
                                //   </Whisper>
                                // )
                                col === "TodaysHigh" ? (
                                  <Whisper
                                    placement="top"
                                    trigger="hover"
                                    speaker={
                                      <Tooltip
                                        className="common__tooltip"
                                        style={{ maxWidth: "400px" }}
                                      >
                                        {tr("todaysHighLowTitle")}:
                                        <br />
                                        - {tr("todaysHighLowTopDetail")}
                                        <br />- {tr("todaysHighLowBottomDetail")}
                                      </Tooltip>
                                    }
                                  >
                                    <>{tr("todaysHighLow")}</>
                                  </Whisper>
                                ) : col === "TodaysLow" ? (
                                  <></>
                                ) : col === "Previous Close" ? (
                                  <Whisper
                                    placement="top"
                                    trigger="hover"
                                    speaker={
                                      <Tooltip
                                        className="common__tooltip"
                                        style={{ maxWidth: "400px" }}
                                      >
                                        {tr("pcAbTitle")}:
                                        <br />
                                        - {tr("pcAbTop")}
                                        <br />- {tr("pcAbBottom")}
                                      </Tooltip>
                                    }
                                  >
                                    <>{tr("pcAbShort")}</>
                                  </Whisper>
                                ) : col === "Net Quantity" ? (
                                  <Whisper
                                    placement="top"
                                    trigger="hover"
                                    speaker={
                                      <Tooltip
                                        className="common__tooltip"
                                        style={{ maxWidth: "400px" }}
                                      >
                                        {tr("netBuyQtyTitle")}:
                                        <br />
                                        - {tr("netBuyQtyTop")}
                                        <br />- {tr("netBuyQtyBottom")}
                                      </Tooltip>
                                    }
                                  >
                                    <>{tr("netBuyQtyShort")}</>
                                  </Whisper>
                                ) : col === "Current Price" ? (
                                  <Whisper
                                    placement="top"
                                    trigger="hover"
                                    speaker={
                                      <Tooltip
                                        className="common__tooltip"
                                        style={{ maxWidth: "400px" }}
                                      >
                                        {tr("currentPrice")}:
                                        <br />- {tr("currentPriceTooltip")}
                                      </Tooltip>
                                    }
                                  >
                                    <>{tr("currentPrice")}</>
                                  </Whisper>
                                ) : col === "Current Value" ? (
                                  <Whisper
                                    placement="top"
                                    trigger="hover"
                                    speaker={
                                      <Tooltip
                                        className="common__tooltip"
                                        style={{ maxWidth: "400px" }}
                                      >
                                        {tr("currentBuyValueTitle")}:
                                        <br />
                                        - {tr("currentBuyValueTop")}
                                        <br />- {tr("currentBuyValueBottom")}
                                      </Tooltip>
                                      // <Tooltip className="common__tooltip" style={{ maxWidth: '400px' }}>
                                      //   Today's High & Low:<br />
                                      //   - Top (H): The highest price at which this futures contract has traded during the current trading session<br />
                                      //   - Bottom (L): The lowest price at which this futures contract has traded during the current trading session
                                      // </Tooltip>
                                    }
                                  >
                                    <>{tr("currentBuyValueShort")}</>
                                  </Whisper>
                                ) : col === "Avg Buy Price" ? (
                                  <></>
                                ) : col === "Buy Value" ? (
                                  <></>
                                ) : // : col === "Buy Value" ? (
                                  //   <Whisper
                                  //     placement="top"
                                  //     trigger="hover"
                                  //     speaker={
                                  //       <Tooltip className="common__tooltip" style={{ maxWidth: '400px' }}>
                                  //         Buy Value:<br />
                                  //         - Total amount you spent on all buy transactions for this futures contract.
                                  //       </Tooltip>
                                  //     }
                                  //   >
                                  //     <>
                                  //       Buy Value
                                  //     </>
                                  //   </Whisper>
                                  // )

                                  col === "Buy Quantity" ? (
                                    <></>
                                  ) : col === "Profit/Loss" ? (
                                    <Whisper
                                      placement="top"
                                      trigger="hover"
                                      speaker={
                                        <Tooltip
                                          className="common__tooltip"
                                          style={{ maxWidth: "400px" }}
                                        >
                                          {tr("profitLoss")}:
                                          <br />- {tr("profitLossTooltip")}
                                        </Tooltip>
                                      }
                                    >
                                      <>{tr("profitLoss")}</>
                                    </Whisper>
                                  ) : col === "Sell Quantity" ? (
                                    <Whisper
                                      placement="top"
                                      trigger="hover"
                                      speaker={
                                        <Tooltip
                                          className="common__tooltip"
                                          style={{ maxWidth: "400px" }}
                                        >
                                          {tr("sellQuantity")}:
                                          <br />- {tr("sellQuantityTooltip")}
                                        </Tooltip>
                                      }
                                    >
                                      <>{tr("sellQuantity")}</>
                                    </Whisper>
                                  ) : col === "Sell Value" ? (
                                    <Whisper
                                      placement="top"
                                      trigger="hover"
                                      speaker={
                                        <Tooltip
                                          className="common__tooltip"
                                          style={{ maxWidth: "400px" }}
                                        >
                                          {tr("sellValue")}:
                                          <br />- {tr("sellValueTooltip")}
                                        </Tooltip>
                                      }
                                    >
                                      <>{tr("sellValue")}</>
                                    </Whisper>
                                  ) : col === "Avg Sell Price" ? (
                                    <Whisper
                                      placement="top"
                                      trigger="hover"
                                      speaker={
                                        <Tooltip
                                          className="common__tooltip"
                                          style={{ maxWidth: "400px" }}
                                        >
                                          {tr("averageSellPrice")}:
                                          <br />- {tr("averageSellPriceTooltip")}
                                        </Tooltip>
                                      }
                                    >
                                      <>{tr("averageSellPrice")}</>
                                    </Whisper>
                                  ) : col === "Target Price" ? (
                                    <Whisper
                                      placement="top"
                                      trigger="hover"
                                      speaker={
                                        <Tooltip
                                          className="common__tooltip"
                                          style={{ maxWidth: "400px" }}
                                        >
                                          {tr("targetLabel")}:
                                          <br />
                                          - {tr("initialTargetPriceTop")}
                                          <br />- {tr("initialTargetPriceBottom")}
                                          <br />- {tr("targetPercentTooltip")}
                                        </Tooltip>
                                      }
                                    >
                                      <>{tr("targetLabel")}</>
                                    </Whisper>
                                  ) : col === "Target %" ? (
                                    <></>
                                  ) : (
                                    translateColumnLabel(col, currentLanguage)
                                  )}
                            </th>
                          ))}
                        </tr>
                      </thead>

                      <tbody>
                        {holdings.length === 0 ? (
                          <tr>
                            <td
                              colSpan={columnOrder.length}
                              className="table-empty-state-cell"
                            >
                              <span className="table-empty-order-text">
                                {tr("noHoldingsDataAvailable")}
                              </span>
                            </td>
                          </tr>
                        ) : currentItems.length === 0 ? (
                          <tr>
                            <td
                              colSpan={columnOrder.length}
                              className="table-empty-state-cell"
                            >
                              {tr("noMatchFound")}
                            </td>
                          </tr>
                        ) : (
                          currentItems.map((holding, index) => {
                            const netQtyForRow = Number(
                              holding.NetQty ??
                              holding.net_qty ??
                              holding.netQuantity ??
                              holding.quantity ??
                              0,
                            );
                            const buyQtyForRow = Number(
                              holding.BuyQty ??
                              holding.buy_qty ??
                              holding.buyQuantity ??
                              holding.BodQty ??
                              0,
                            );

                            // Skip only when both are truly zero after all fallbacks.
                            if (netQtyForRow === 0 && buyQtyForRow === 0) {
                              return null;
                            }

                            const target = parseFloat(holding.targetPrice);
                            const initialTargetLogic =
                              holding.target_logic || "higher_target";
                            const scripCodeForRow =
                              holding.ScripCode ||
                              holding.scripcode ||
                              holding.token ||
                              holding.symboltoken;
                            const cachedLivePrice = parseFloat(
                              livePriceCache.current?.[scripCodeForRow],
                            );
                            const sdRow = holding?.stocks_data || {};
                            const livePrice = !isNaN(cachedLivePrice) &&
                              cachedLivePrice > 0
                              ? cachedLivePrice
                              : parseFloat(
                                  holding.LTP ??
                                    sdRow?.close ??
                                    sdRow?.last_price ??
                                    sdRow?.ohlc?.close ??
                                    0,
                                );

                            let percent = null;
                            if (
                              holding?.hasTargetData &&
                              !isNaN(target) &&
                              target !== 0 &&
                              !isNaN(livePrice)
                            ) {
                              if (initialTargetLogic === "lower_target") {
                                percent =
                                  100 - ((livePrice - target) / target) * 100;
                              } else {
                                percent =
                                  ((livePrice - target) / target) * 100 + 100;
                              }
                            }

                            const bgRgb =
                              percent !== null && percent >= 100
                                ? "201,255,219"
                                : percent !== null && percent >= 90
                                  ? "226,255,237"
                                  : undefined;
                            const shouldHighlight = bgRgb !== undefined;
                            return (
                              <tr
                                key={holding.Symbol || index}
                                className={
                                  shouldHighlight
                                    ? "position-target-gradient-row"
                                    : undefined
                                }
                                style={{ "--target-bg-rgb": bgRgb }}
                              >
                                {columnOrder.map((col) => {
                                  let content;

                                  if (col === "Name") {
                                    const fullName = (
                                      holding.FullName ||
                                      holding.ScripName ||
                                      ""
                                    ).trim();
                                    const exchShort =
                                      holding.Exch === "N"
                                        ? "NSE"
                                        : holding.Exch === "B"
                                          ? "BSE"
                                          : holding.Exch === "M"
                                            ? "MCX"
                                            : "BSE";
                                    const exchSegment =
                                      holding.ExchType === "D"
                                        ? "Derivative"
                                        : "Cash";
                                    const exchFullLabel = `${exchShort} ${exchSegment}`;
                                    const scripName = String(
                                      holding.ScripName ||
                                        holding.Symbol ||
                                        holding.TradingSymbol ||
                                        "",
                                    ).trim();

                                    content = (
                                      <div
                                        className="main_table_company_cell"
                                        style={{
                                          minWidth: 0,
                                          alignItems: "flex-start",
                                        }}
                                      >
                                        <div
                                          style={{
                                            paddingLeft: "10px",
                                            minWidth: 0,
                                            width: "100%",
                                          }}
                                        >
                                          <Whisper
                                            placement="top"
                                            trigger="hover"
                                            speaker={
                                              <Tooltip
                                                className="common__tooltip"
                                                style={{ maxWidth: "400px" }}
                                              >
                                                {fullName}
                                              </Tooltip>
                                            }
                                          >
                                            <h5
                                              className="uppercase"
                                              style={{
                                                margin: 0,
                                                maxWidth: 145,
                                                overflow: "hidden",
                                                textOverflow: "ellipsis",
                                                whiteSpace: "nowrap",
                                                cursor: "default",
                                              }}
                                            >
                                              {fullName}
                                            </h5>
                                          </Whisper>
                                          <ul
                                            className="company_table_tags"
                                            style={{
                                              paddingLeft: 0,
                                              marginTop: 4,
                                              flexWrap: "nowrap",
                                              alignItems: "center",
                                              marginBottom: "0px",
                                              gap: "4px",
                                              minWidth: 0,
                                            }}
                                          >
                                            <li
                                              className="cbse_nse"
                                              style={{
                                                minWidth: 0,
                                                maxWidth: 85,
                                                overflow: "hidden",
                                              }}
                                            >
                                              <Whisper
                                                placement="top"
                                                trigger="hover"
                                                speaker={
                                                  <Tooltip
                                                    className="common__tooltip"
                                                    style={{ maxWidth: "320px" }}
                                                  >
                                                    {scripName || "-"}
                                                  </Tooltip>
                                                }
                                              >
                                                <span
                                                  style={{
                                                    display: "inline-block",
                                                    maxWidth: "100%",
                                                    overflow: "hidden",
                                                    textOverflow: "ellipsis",
                                                    whiteSpace: "nowrap",
                                                    verticalAlign: "bottom",
                                                    cursor: "default",
                                                  }}
                                                >
                                                  {scripName || "-"}
                                                </span>
                                              </Whisper>
                                            </li>
                                            <li
                                              className="cbse_nse"
                                              style={{
                                                minWidth: 0,
                                                flexShrink: 0,
                                              }}
                                            >
                                              <Whisper
                                                placement="top"
                                                trigger="hover"
                                                speaker={
                                                  <Tooltip className="common__tooltip">
                                                    {exchFullLabel}
                                                  </Tooltip>
                                                }
                                              >
                                                <span
                                                  style={{
                                                    cursor: "default",
                                                    whiteSpace: "nowrap",
                                                  }}
                                                >
                                                  {exchShort}
                                                </span>
                                              </Whisper>
                                            </li>
                                            <Whisper
                                              placement="top"
                                              trigger="hover"
                                              speaker={
                                                <Tooltip className="common__tooltip">
                                                  {tr("setTarget")}
                                                </Tooltip>
                                              }
                                            >
                                              <li
                                                className="cset__target"
                                                role="button"
                                                tabIndex={0}
                                                aria-label={tr("setTarget")}
                                                style={{
                                                  zIndex: 30,
                                                  minWidth: "unset",
                                                  padding: "2px 6px",
                                                  cursor: "pointer",
                                                  display: "flex",
                                                  alignItems: "center",
                                                  justifyContent: "center",
                                                  lineHeight: 1,
                                                }}
                                                onClick={(e) => {
                                                  e.stopPropagation();
                                                  openSetTargetModal(holding);
                                                }}
                                                onKeyDown={(e) => {
                                                  if (
                                                    e.key === "Enter" ||
                                                    e.key === " "
                                                  ) {
                                                    e.preventDefault();
                                                    e.stopPropagation();
                                                    openSetTargetModal(holding);
                                                  }
                                                }}
                                              >
                                                <TbTarget size={16} />
                                              </li>
                                            </Whisper>
                                          </ul>
                                        </div>
                                      </div>
                                    );
                                  } else if (
                                    col === "Net Quantity" ||
                                    col === "NetQty"
                                  ) {
                                    const buyQty =
                                      Number(holding.BuyQty) > 0
                                        ? Number(holding.BuyQty)
                                        : Number(
                                          holding.BodQty ??
                                          holding.buy_qty ??
                                          holding.buyQuantity ??
                                          0,
                                        );
                                    content = (
                                      <div
                                        className="trade__price__cell"
                                        style={{
                                          textAlign: "center",
                                          width: "80px",
                                          margin: "auto",
                                        }}
                                      >
                                        <div
                                          style={{
                                            display: "grid",
                                            gridTemplateColumns: "16px 1fr",
                                            alignItems: "center",
                                            justifyItems: "center",
                                            gap: "4px",
                                          }}
                                        >
                                          <span
                                            style={{
                                              fontSize: "0.7em",
                                              opacity: "0.6",
                                              color: "#111827",
                                              fontWeight: "600",
                                            }}
                                          >
                                            N
                                          </span>
                                          <div style={{ textAlign: "center" }}>
                                            {netQtyForRow}
                                          </div>
                                        </div>

                                        <div
                                          style={{
                                            width: "25px",
                                            height: "1px",
                                            background:
                                              "linear-gradient(90deg, transparent, #cbd5e1, transparent)",
                                            margin: "4px auto",
                                            opacity: "0.5",
                                          }}
                                        ></div>

                                        <div
                                          style={{
                                            display: "grid",
                                            gridTemplateColumns: "16px 1fr",
                                            alignItems: "center",
                                            justifyItems: "center",
                                            gap: "4px",
                                          }}
                                        >
                                          <span
                                            style={{
                                              fontSize: "0.7em",
                                              opacity: "0.6",
                                              color: "#2457BF",
                                              fontWeight: "600",
                                            }}
                                          >
                                            B
                                          </span>
                                          <div style={{ textAlign: "center" }}>
                                            {buyQty ?? "-"}
                                          </div>
                                        </div>
                                      </div>
                                    );
                                  }


                                  else if (
                                    col === "Current Price" ||
                                    col === "LTP"
                                  ) {
                                    const scripCode = holding.ScripCode;
                                    const livePrice =
                                      livePriceCache.current[scripCode];
                                    const pClose =
                                      livePCloseCache.current[scripCode];
                                    const stocksData = holding?.stocks_data || {};
                                    const ohlc = stocksData?.ohlc || {};
                                    const currentPrice = Number(
                                      livePrice ??
                                      holding.LTP ??
                                      stocksData?.close ??
                                      stocksData?.last_price ??
                                      stocksData?.LastRate ??
                                      ohlc?.close ??
                                      stocksData?.open ??
                                      ohlc?.open ??
                                      0,
                                    );
                                    const previousClose = Number(
                                      pClose ??
                                      holding.PreviousClose ??
                                      stocksData?.PClose ??
                                      stocksData?.previous_close ??
                                      stocksData?.pc ??
                                      stocksData?.open ??
                                      ohlc?.open ??
                                      0,
                                    );
                                    const hasBoth =
                                      Number.isFinite(currentPrice) &&
                                      Number.isFinite(previousClose) &&
                                      previousClose !== 0;
                                    const netChange = hasBoth
                                      ? currentPrice - previousClose
                                      : 0;
                                    const changePercent = hasBoth
                                      ? (
                                        (netChange / previousClose) *
                                        100
                                      ).toFixed(2)
                                      : "0.00";
                                    const isPositive = hasBoth
                                      ? currentPrice >= previousClose
                                      : true;

                                    content = (
                                      <div
                                        className={`trade__price__cell ${isPositive ? "text_success_color" : "text_danger_color"}`}
                                        style={{
                                          textAlign: "center",
                                          width: "140px",
                                          margin: "auto",
                                        }}
                                      >
                                        <div>₹{formatNumber(currentPrice)}</div>
                                        <div style={{ marginTop: "4px" }}>
                                          <i
                                            className={`icon-arrow-${isPositive ? "up" : "down"}`}
                                          ></i>{" "}
                                          ₹
                                          {formatNumber(
                                            Math.abs(netChange).toFixed(2),
                                          )}{" "}
                                          ({changePercent}%)
                                        </div>
                                      </div>
                                    );
                                  }


                                  else if (col === "TodaysHigh") {
                                    const scripCode = holding.ScripCode;
                                    const stocksDataHl = holding?.stocks_data || {};
                                    const ohlcHl = stocksDataHl?.ohlc || {};

                                    const liveDayHigh = Number(
                                      liveHighCache.current[scripCode],
                                    );
                                    const mappedDayHigh = Number(
                                      holding.TodaysHigh ??
                                      holding.DayHigh ??
                                      stocksDataHl?.high ??
                                      stocksDataHl?.High ??
                                      ohlcHl?.high,
                                    );
                                    const currentDayHigh = Number.isFinite(liveDayHigh)
                                      ? liveDayHigh
                                      : Number.isFinite(mappedDayHigh)
                                        ? mappedDayHigh
                                        : 0;

                                    const liveDayLow = Number(
                                      liveLowCache.current[scripCode],
                                    );
                                    const mappedDayLow = Number(
                                      holding.TodaysLow ??
                                      holding.DayLow ??
                                      stocksDataHl?.low ??
                                      stocksDataHl?.Low ??
                                      ohlcHl?.low,
                                    );
                                    const currentDayLow = Number.isFinite(liveDayLow)
                                      ? liveDayLow
                                      : Number.isFinite(mappedDayLow)
                                        ? mappedDayLow
                                        : 0;

                                    const livePrice = Number(
                                      livePriceCache.current[scripCode] ??
                                      holding.LTP ??
                                      stocksDataHl?.close ??
                                      stocksDataHl?.last_price ??
                                      ohlcHl?.close ??
                                      stocksDataHl?.open ??
                                      ohlcHl?.open,
                                    );

                                    const isNearHigh =
                                      currentDayHigh > 0 &&
                                      Number.isFinite(livePrice) &&
                                      Math.abs(
                                        ((livePrice - currentDayHigh) /
                                          currentDayHigh) *
                                        100,
                                      ) < 0.5;
                                    const isNearLow =
                                      currentDayLow > 0 &&
                                      Number.isFinite(livePrice) &&
                                      Math.abs(
                                        ((livePrice - currentDayLow) /
                                          currentDayLow) *
                                        100,
                                      ) < 0.5;

                                    content = (
                                      <div
                                        className="trade__price__cell"
                                        style={{
                                          textAlign: "center",
                                          width: "100px",
                                          margin: "auto",
                                        }}
                                      >
                                        {/* Today's High (Top) */}
                                        <div
                                          className={
                                            isNearHigh ? "text_warning_color" : ""
                                          }
                                          style={{
                                            display: "grid",
                                            gridTemplateColumns: "16px 1fr",
                                            alignItems: "center",
                                            justifyItems: "center",
                                            gap: "4px",
                                          }}
                                        >
                                          <span
                                            style={{
                                              fontSize: "0.7em",
                                              opacity: "0.6",
                                              color: "#16A34A",
                                              fontWeight: "600",
                                            }}
                                          >
                                            H
                                          </span>
                                          <div style={{ textAlign: "center" }}>
                                            ₹{formatNumber(currentDayHigh)}
                                            {isNearHigh && (
                                              <i
                                                className="icon-arrow-up"
                                                style={{
                                                  color: "#FFA726",
                                                  marginLeft: "4px",
                                                }}
                                              ></i>
                                            )}
                                          </div>
                                        </div>

                                        {/* Separator Line */}
                                        <div
                                          style={{
                                            width: "25px",
                                            height: "1px",
                                            background:
                                              "linear-gradient(90deg, transparent, #cbd5e1, transparent)",
                                            margin: "4px auto",
                                            opacity: "0.5",
                                          }}
                                        ></div>

                                        {/* Today's Low (Bottom) */}
                                        <div
                                          className={
                                            isNearLow ? "text_danger_color" : ""
                                          }
                                          style={{
                                            display: "grid",
                                            gridTemplateColumns: "16px 1fr",
                                            alignItems: "center",
                                            justifyItems: "center",
                                            gap: "4px",
                                          }}
                                        >
                                          <span
                                            style={{
                                              fontSize: "0.7em",
                                              opacity: "0.6",
                                              color: "#DC2626",
                                              fontWeight: "600",
                                            }}
                                          >
                                            L
                                          </span>
                                          <div style={{ textAlign: "center" }}>
                                            ₹{formatNumber(currentDayLow)}
                                            {isNearLow && (
                                              <i
                                                className="icon-arrow-down"
                                                style={{
                                                  color: "#EF5350",
                                                  marginLeft: "4px",
                                                }}
                                              ></i>
                                            )}
                                          </div>
                                        </div>
                                      </div>
                                    );
                                  }

                                  // TodaysLow Column
                                  else if (col === "TodaysLow") {
                                    content = <></>;
                                  } else if (col === "Current Value") {
                                    const scripCode = holding.ScripCode;
                                    const livePrice =
                                      livePriceCache.current[scripCode];
                                    const pClose =
                                      livePCloseCache.current[scripCode];
                                    const stocksDataCv = holding?.stocks_data || {};
                                    const ohlcCv = stocksDataCv?.ohlc || {};
                                    const currentPrice = Number(
                                      livePrice ??
                                      holding.LTP ??
                                      stocksDataCv?.close ??
                                      stocksDataCv?.last_price ??
                                      ohlcCv?.close ??
                                      stocksDataCv?.open ??
                                      0,
                                    );
                                    const previousClose = Number(
                                      pClose ??
                                      holding.PreviousClose ??
                                      stocksDataCv?.PClose ??
                                      stocksDataCv?.previous_close ??
                                      stocksDataCv?.open ??
                                      ohlcCv?.open ??
                                      0,
                                    );
                                    const isPositive =
                                      Number.isFinite(currentPrice) &&
                                        Number.isFinite(previousClose)
                                        ? currentPrice >= previousClose
                                        : true;
                                    const Multiplier = holding.Multiplier;

                                    const currentValue =
                                      currentPrice * netQtyForRow * Multiplier;
                                    const buyVal =
                                      holding.Multiplier *
                                      netQtyForRow *
                                      (holding.BuyQty > 0
                                        ? holding.BuyAvgRate
                                        : holding.AvgRate);

                                    content = (
                                      <div
                                        className="trade__price__cell"
                                        style={{
                                          textAlign: "center",
                                          width: "120px",
                                          margin: "auto",
                                        }}
                                      >
                                        {/* Current Value (Top) */}
                                        <div
                                          className={
                                            isPositive
                                              ? "text_success_color"
                                              : "text_danger_color"
                                          }
                                          style={{
                                            display: "grid",
                                            gridTemplateColumns: "16px 1fr",
                                            alignItems: "center",
                                            justifyItems: "center",
                                            gap: "4px",
                                          }}
                                        >
                                          <span
                                            style={{
                                              fontSize: "0.7em",
                                              opacity: "0.6",
                                              fontWeight: "600",
                                            }}
                                          >
                                            C
                                          </span>
                                          <div style={{ textAlign: "center" }}>
                                            ₹{formatNumber(currentValue)}
                                            <i
                                              className={`icon-arrow-${isPositive ? "up" : "down"}`}
                                              style={{ marginLeft: "4px" }}
                                            ></i>
                                          </div>
                                        </div>

                                        {/* Separator Line */}
                                        <div
                                          style={{
                                            width: "25px",
                                            height: "1px",
                                            background:
                                              "linear-gradient(90deg, transparent, #cbd5e1, transparent)",
                                            margin: "4px auto",
                                            opacity: "0.5",
                                          }}
                                        ></div>

                                        {/* Buy Value (Bottom) */}
                                        <div
                                          style={{
                                            display: "grid",
                                            gridTemplateColumns: "16px 1fr",
                                            alignItems: "center",
                                            justifyItems: "center",
                                            gap: "4px",
                                          }}
                                        >
                                          <span
                                            style={{
                                              fontSize: "0.7em",
                                              opacity: "0.6",
                                              fontWeight: "600",
                                            }}
                                          >
                                            B
                                          </span>
                                          <div style={{ textAlign: "center" }}>
                                            ₹{formatNumber(buyVal?.toFixed(2))}
                                          </div>
                                        </div>
                                      </div>
                                    );
                                  }

                                  // Buy Value Column
                                  else if (col === "Buy Value") {
                                    content = <></>;
                                  } else if (col === "Previous Close") {
                                    const previousCloseText = formatNumber(
                                      holding.PreviousClose,
                                    );
                                    const avgBuyRaw =
                                      holding.BuyQty > 0
                                        ? holding.BuyAvgRate
                                        : holding.AvgRate;
                                    const avgBuyNum = Number(avgBuyRaw);
                                    const avgBuyText = Number.isNaN(avgBuyNum)
                                      ? "-"
                                      : formatNumber(avgBuyNum);

                                    content = (
                                      <div
                                        className="trade__price__cell"
                                        style={{
                                          textAlign: "center",
                                          width: "110px",
                                          margin: "auto",
                                        }}
                                      >
                                        <div
                                          style={{
                                            display: "grid",
                                            gridTemplateColumns: "20px 1fr",
                                            alignItems: "center",
                                            justifyItems: "center",
                                            gap: "4px",
                                          }}
                                        >
                                          <span
                                            style={{
                                              fontSize: "0.7em",
                                              opacity: 0.6,
                                              color: "#111827",
                                              fontWeight: "600",
                                            }}
                                          >
                                            PC
                                          </span>
                                          <div style={{ textAlign: "center" }}>
                                            ₹{previousCloseText}
                                          </div>
                                        </div>

                                        <div
                                          style={{
                                            width: "25px",
                                            height: "1px",
                                            background:
                                              "linear-gradient(90deg, transparent, #cbd5e1, transparent)",
                                            margin: "4px auto",
                                            opacity: "0.5",
                                          }}
                                        />

                                        <div
                                          style={{
                                            display: "grid",
                                            gridTemplateColumns: "20px 1fr",
                                            alignItems: "center",
                                            justifyItems: "center",
                                            gap: "4px",
                                          }}
                                        >
                                          <span
                                            style={{
                                              fontSize: "0.7em",
                                              opacity: 0.6,
                                              color: "#2457BF",
                                              fontWeight: "600",
                                            }}
                                          >
                                            AB
                                          </span>
                                          <div style={{ textAlign: "center" }}>
                                            ₹{avgBuyText}
                                          </div>
                                        </div>
                                      </div>
                                    );
                                  } else if (
                                    col === "Avg Buy Price" ||
                                    col === "BuyAvgRate" ||
                                    col === "AvgRate"
                                  ) {
                                    content = <></>;
                                  } else if (
                                    col === "Profit/Loss" ||
                                    col === "PL"
                                  ) {
                                    const mtomValue = calculateMTOM(holding);
                                    const pl = parseFloat(mtomValue) || 0;
                                    const isProfit = pl >= 0;

                                    content = (
                                      <div
                                        className={`trade__price__cell ${isProfit ? "text_success_color" : "text_danger_color"}`}
                                        style={{
                                          textAlign: "center",
                                          width: "110px",
                                          margin: "auto",
                                        }}
                                      >
                                        ₹{formatNumber(pl.toFixed(2))}
                                        <i
                                          className={`icon-arrow-${isProfit ? "up" : "down"}`}
                                        ></i>
                                      </div>
                                    );
                                  } else if (
                                    col === "Buy Quantity" ||
                                    col === "BuyQty"
                                  ) {
                                    content = <></>;
                                  } else if (
                                    col === "Sell Quantity" ||
                                    col === "SellQty"
                                  ) {
                                    content = (
                                      <div
                                        style={{
                                          textAlign: "center",
                                          width: "60px",
                                          margin: "auto",
                                        }}
                                      >
                                        {holding.SellQty}
                                      </div>
                                    );
                                  } else if (
                                    col === "Avg Sell Price" ||
                                    col === "SellAvgRate"
                                  ) {
                                    content = (
                                      <div
                                        style={{
                                          textAlign: "center",
                                          width: "110px",
                                          margin: "auto",
                                        }}
                                      >
                                        {holding.SellAvgRate}
                                      </div>
                                    );
                                  } else if (col === "Sell Value") {
                                    content = (
                                      <div
                                        style={{
                                          textAlign: "center",
                                          width: "110px",
                                          margin: "auto",
                                        }}
                                      >
                                        ₹{formatNumber(holding.SellValue)}
                                      </div>
                                    );
                                  } else if (
                                    col === "Target Price" ||
                                    col === "targetPrice"
                                  ) {
                                    const initialPrice = holding.initialPrice;
                                    const targetPrice = holding.targetPrice;
                                    const hasTargetData = !!holding?.hasTargetData;
                                    const target = parseFloat(holding.targetPrice);
                                    const initial = parseFloat(holding.initialPrice);
                                    const scripCode =
                                      holding.ScripCode ||
                                      holding.scripcode ||
                                      holding.token ||
                                      holding.symboltoken;
                                    const livePrice = parseFloat(
                                      livePriceCache.current?.[scripCode],
                                    );
                                    const currentPrice =
                                      !isNaN(livePrice) && livePrice > 0
                                        ? livePrice
                                        : parseFloat(holding.LTP ?? 0);
                                    const targetLogic =
                                      holding.target_logic || "higher_target";
                                    const reasonText =
                                      holding.targetReason ||
                                      holding.target_reason ||
                                      holding.reason ||
                                      "";
                                    const reasonDetailsText =
                                      holding.targetReasonDetails ||
                                      holding.target_reason_details ||
                                      holding.reason_details ||
                                      "";
                                    const hasReason = !!String(reasonText).trim();

                                    const isTargetInvalid =
                                      !hasTargetData ||
                                      isNaN(target) ||
                                      target <= 0 ||
                                      isNaN(initial) ||
                                      target === initial;

                                    const getTargetColor = (percentage) => {
                                      const clampedPercent = Math.max(
                                        0,
                                        Math.min(100, percentage),
                                      );
                                      const colors = [
                                        { max: 10, color: "#C62828" },
                                        { max: 20, color: "#D84315" },
                                        { max: 30, color: "#E65100" },
                                        { max: 40, color: "#EF6C00" },
                                        { max: 50, color: "#F57C00" },
                                        { max: 60, color: "#F9A825" },
                                        { max: 70, color: "#AFB42B" },
                                        { max: 80, color: "#7CB342" },
                                        { max: 90, color: "#43A047" },
                                        { max: 100, color: "#2E7D32" },
                                      ];
                                      const range = colors.find(
                                        (c) => clampedPercent <= c.max,
                                      );
                                      return range ? range.color : "#1B5E20";
                                    };

                                    let percent = null;
                                    let percentTextColor = "#64748B";
                                    if (!isTargetInvalid && !isNaN(currentPrice)) {
                                      percent =
                                        targetLogic === "lower_target"
                                          ? 100 -
                                            ((currentPrice - target) / target) *
                                              100
                                          : ((currentPrice - target) / target) *
                                              100 +
                                            100;
                                      if (Number.isFinite(percent)) {
                                        percentTextColor = getTargetColor(percent);
                                      }
                                    }

                                    const progressWidth = Number.isFinite(percent)
                                      ? Math.min(100, Math.max(0, percent))
                                      : 0;

                                    const reasonButton = hasReason ? (
                                      <Whisper
                                        placement="top"
                                        trigger="hover"
                                        speaker={
                                          <Tooltip
                                            className="common__tooltip"
                                            style={{ maxWidth: "320px" }}
                                          >
                                            <div>
                                              <b>Reason:</b>{" "}
                                              {String(reasonText).trim()}
                                            </div>
                                            <div style={{ marginTop: "4px" }}>
                                              <b>Details:</b>{" "}
                                              {String(reasonDetailsText).trim() ||
                                                "No additional details provided."}
                                            </div>
                                          </Tooltip>
                                        }
                                      >
                                        <button
                                          type="button"
                                          data-bs-toggle="modal"
                                          data-bs-target="#TargetReasonDetailsModal"
                                          aria-label="View target reason details"
                                          onClick={(e) => {
                                            e.stopPropagation();
                                            setTargetReasonModalData({
                                              stockName:
                                                holding.FullName ||
                                                holding.ScripName ||
                                                holding.Symbol ||
                                                "-",
                                              reason: String(reasonText).trim(),
                                              details: String(
                                                reasonDetailsText,
                                              ).trim(),
                                            });
                                          }}
                                          style={{
                                            width: "18px",
                                            height: "18px",
                                            minWidth: "18px",
                                            borderRadius: "50%",
                                            border: "1px solid #CBD5E1",
                                            background: "#FFFFFF",
                                            color: "#475569",
                                            fontSize: "10px",
                                            fontWeight: 700,
                                            lineHeight: 1,
                                            display: "inline-flex",
                                            alignItems: "center",
                                            justifyContent: "center",
                                            cursor: "pointer",
                                            padding: 0,
                                            flexShrink: 0,
                                          }}
                                        >
                                          i
                                        </button>
                                      </Whisper>
                                    ) : null;

                                    if (isTargetInvalid) {
                                      content = (
                                        <div
                                          style={{
                                            display: "flex",
                                            justifyContent: "center",
                                            padding: "4px 0",
                                          }}
                                        >
                                          <Whisper
                                            placement="top"
                                            trigger="hover"
                                            speaker={
                                              <Tooltip className="common__tooltip">
                                                {tr("setTarget")}
                                              </Tooltip>
                                            }
                                          >
                                            <button
                                              type="button"
                                              className="p-0 border-0"
                                              style={{
                                                display: "inline-flex",
                                                alignItems: "center",
                                                gap: "6px",
                                                padding: "7px 14px",
                                                borderRadius: "999px",
                                                border: "1px solid #BAD0FD",
                                                background:
                                                  "linear-gradient(135deg, #EBF2FA 0%, #F8FAFC 100%)",
                                                color: "#2457BF",
                                                fontSize: "11px",
                                                fontWeight: "600",
                                                cursor: "pointer",
                                                boxShadow:
                                                  "0 2px 8px rgba(36, 87, 191, 0.12)",
                                              }}
                                              onClick={(e) => {
                                                e.stopPropagation();
                                                openSetTargetModal(
                                                  holding,
                                                  currentPrice,
                                                );
                                              }}
                                              aria-label={tr("setTarget")}
                                            >
                                              <TbTarget size={14} />
                                              {tr("setTarget")}
                                            </button>
                                          </Whisper>
                                        </div>
                                      );
                                    } else {
                                      content = (
                                        <div
                                          style={{
                                            minWidth: "100px",
                                            margin: "0 auto",
                                            padding: "9px 10px",
                                            // borderRadius: "12px",
                                            // background:
                                            //   "linear-gradient(180deg, #FFFFFF 0%, #F8FAFC 100%)",
                                            // border: "1px solid #E2E8F0",
                                            // boxShadow:
                                            //   "0 2px 8px rgba(15, 23, 42, 0.05)",
                                          }}
                                        >
                                          <div
                                            style={{
                                              display: "flex",
                                              alignItems: "stretch",
                                              gap: "8px",
                                              marginBottom: "8px",
                                            }}
                                          >
                                            <div style={{ flex: 1, minWidth: 0 }}>
                                              <div
                                                style={{
                                                  fontSize: "9px",
                                                  fontWeight: "700",
                                                  color: "#94A3B8",
                                                  letterSpacing: "0.06em",
                                                  textTransform: "uppercase",
                                                  marginBottom: "2px",
                                                }}
                                              >
                                                Initial
                                              </div>
                                              <div
                                                style={{
                                                  fontSize: "12px",
                                                  fontWeight: "700",
                                                  color: "#2457BF",
                                                  whiteSpace: "nowrap",
                                                }}
                                              >
                                                ₹{formatNumber(initialPrice)}
                                              </div>
                                            </div>
                                            <div
                                              style={{
                                                width: "1px",
                                                background: "#E2E8F0",
                                                alignSelf: "stretch",
                                              }}
                                            />
                                            <div style={{ flex: 1, minWidth: 0 }}>
                                              <div
                                                style={{
                                                  fontSize: "9px",
                                                  fontWeight: "700",
                                                  color: "#94A3B8",
                                                  letterSpacing: "0.06em",
                                                  textTransform: "uppercase",
                                                  marginBottom: "2px",
                                                }}
                                              >
                                                Target
                                              </div>
                                              <div
                                                style={{
                                                  fontSize: "12px",
                                                  fontWeight: "700",
                                                  color: "#D67A1F",
                                                  whiteSpace: "nowrap",
                                                }}
                                              >
                                                ₹{formatNumber(targetPrice)}
                                              </div>
                                            </div>
                                          </div>

                                          {/* <div
                                            style={{
                                              height: "6px",
                                              borderRadius: "999px",
                                              background: "#E2E8F0",
                                              overflow: "hidden",
                                              marginBottom: "6px",
                                            }}
                                          >
                                            <div
                                              style={{
                                                height: "100%",
                                                width: `${progressWidth}%`,
                                                borderRadius: "999px",
                                                background: `linear-gradient(90deg, ${percentTextColor}99, ${percentTextColor})`,
                                                transition: "width 0.25s ease",
                                              }}
                                            />
                                          </div> */}

                                          <div
                                            style={{
                                              display: "flex",
                                              alignItems: "center",
                                              justifyContent: "center",
                                              gap: "6px",
                                            }}
                                          >
                                            <span
                                              style={{
                                                display: "inline-flex",
                                                alignItems: "center",
                                                padding: "2px 8px",
                                                borderRadius: "999px",
                                                // background: `${percentTextColor}14`,
                                                color: percentTextColor,
                                                fontSize: "11px",
                                                fontWeight: "700",
                                                lineHeight: 1.4,
                                              }}
                                            >
                                              {Number.isFinite(percent)
                                                ? `${percent.toFixed(1)}%`
                                                : "—"}
                                            </span>
                                            {reasonButton}
                                          </div>
                                        </div>
                                      );
                                    }
                                  } else if (
                                    col === "Target %" ||
                                    col === "TargetPercent"
                                  ) {
                                    content = (
                                      <div style={{ display: "none" }} />
                                    );
                                  } else if (col === "Actions") {
                                    content = (
                                      <ul
                                        onClick={(e) => {
                                          e.stopPropagation();
                                        }}
                                        className="table__action_hover"
                                        style={{ top: "0", paddingRight: "10px", border: "none", height: "98%" }}
                                      >
                                        <li>
                                          <div className="buy-sell-segmented">
                                            <button
                                              className="btn_buy wl-premium-action-btn wl-premium-buy"
                                              type="button"
                                              data-bs-toggle="modal"
                                              data-bs-target="#BuySellModal"
                                              onClick={() =>
                                                SettingBuyModalDetails(
                                                  holding,
                                                  "Buy",
                                                )
                                              }
                                            >
                                              {tr("buy")}
                                            </button>

                                            <button
                                              className="btn_sell wl-premium-action-btn wl-premium-sell"
                                              type="button"
                                              data-bs-toggle="modal"
                                              data-bs-target="#BuySellModal"
                                              onClick={() =>
                                                SettingBuyModalDetails(
                                                  holding,
                                                  "Sell",
                                                )
                                              }
                                            >
                                              {tr("sell")}
                                            </button>
                                          </div>
                                        </li>
                                        {activeBrokerFlag !== "grow" && (
                                          <Whisper
                                            placement="top"
                                            trigger="hover"
                                            speaker={
                                              <Tooltip className="common__tooltip">
                                                {tr("marketDepth")}
                                              </Tooltip>
                                            }
                                          >
                                            <li>
                                              <button
                                                ref={marketDepthButtonRef}
                                                onClick={(e) =>
                                                  handleMarketDepthClick(
                                                    holding,
                                                    e,
                                                  )
                                                }
                                                className="btn_trading wl-premium-action-btn wl-premium-depth"
                                                style={{
                                                  width: "38px",
                                                  height: "34px",
                                                  borderRadius: "8px",
                                                  border: "none",
                                                  background: "transparent",
                                                  display: "inline-flex",
                                                  alignItems: "center",
                                                  justifyContent: "center",
                                                  padding: 0,
                                                }}
                                              >
                                                <MarketDepthIcon />
                                              </button>
                                            </li>
                                          </Whisper>
                                        )}
                                      </ul>
                                    );
                                  } else {
                                    content = holding[col];
                                    if (content === null || content === undefined)
                                      content = "-";
                                    content = (
                                      <div
                                        style={{
                                          textAlign:
                                            col === "Name"
                                              ? "left"
                                              : ["ProfitPct", "Volume"].includes(
                                                col,
                                              )
                                                ? "center"
                                                : "center",
                                        }}
                                      >
                                        {content}
                                      </div>
                                    );
                                  }

                                  return (
                                    <td
                                      key={col}
                                      onClick={() => {
                                        if (supportsCandleChart(broker_type)) {
                                          handleRowClick(holding);
                                        }
                                      }}
                                     
                                      className={
                                        col === "Actions"
                                          ? "action-cell"
                                          : undefined
                                      }
                                      style={{
                                        cursor: supportsCandleChart(broker_type)
                                          ? "pointer"
                                          : undefined,
                                        textAlign:
                                          col === "Name"
                                            ? "left"
                                            : ["ProfitPct", "Volume"].includes(
                                              col,
                                            )
                                              ? "center"
                                              : "center",
                                        paddingLeft:
                                          col === "Actions" ? 0 : undefined,
                                        paddingRight:
                                          col === "Actions" ? 0 : undefined,
                                        padding:
                                          col === "Name" ? "10px" : undefined,
                                        display: [
                                          "TodaysLow",
                                          "Buy Quantity",
                                          "Target %",
                                          "Avg Buy Price",
                                          "Buy Value",
                                        ].includes(col)
                                          ? "none"
                                          : undefined,
                                      }}
                                    >
                                      {content}
                                    </td>
                                  );
                                })}
                              </tr>
                            ); // Remove ")" and return statement before <tr> and the condition above
                          })
                        )}

                        {filteredHoldings.length > 1 && (
                          <tr
                            style={{
                              backgroundColor: "#DEF5EA",
                              fontWeight: "600",
                            }}
                          >
                            {columnOrder.map((col) => {
                              let content = null;

                              // if (col === "Buy Value") {
                              //   const totalBuyValue = filteredHoldings.reduce((sum, h) => {
                              //     const buyVal = h.NetQty * (h.BuyQty > 0 ? h.BuyAvgRate : h.AvgRate || 0);
                              //     return sum + (buyVal || 0);
                              //   }, 0);
                              //   content = `₹${formatNumber(totalBuyValue.toFixed(2))}`;

                              //   const totalPL = filteredHoldings.reduce((sum, h) => sum + calculateMTOM(h), 0);
                              //   content = (
                              //     <span
                              //       style={{
                              //         display: "inline-block",
                              //         padding: "4px 12px",
                              //         borderRadius: "10px",
                              //         backgroundColor: totalPL < 0 ? "#fff5f5ff" : "#F6FCF9",
                              //         border:totalPL < 0 ? "1px solid #FBB1B1" : "1px solid #d4d4d4ff",
                              //         color: totalPL < 0 ? "red" : "green",
                              //         minWidth: "60px",
                              //         textAlign: "center",
                              //       }}
                              //     >
                              //       {content}
                              //     </span>
                              //   );
                              // }

                              //         if (col === "Current Value") {
                              //           const totalCurrentValue = filteredHoldings.reduce((sum, h) => {
                              //             const scripCode = h.ScripCode;
                              //             const livePrice = parseFloat(livePriceCache.current[scripCode] || h.LTP || 0);
                              //             const value = livePrice * (h.NetQty || 0);
                              //             const Multiplier =  (h.Multiplier || 1);
                              //             return sum + (value*Multiplier);
                              //           }, 0);
                              //            const totalPL = filteredHoldings.reduce((sum, h) => sum + calculateMTOM(h), 0);

                              //           content = (
                              //             <span
                              //               style={{
                              //                 display: "inline-block",
                              //                 padding: "4px 12px",
                              //                 borderRadius: "10px",
                              //                 backgroundColor: totalPL < 0 ? "#fff5f5ff" : "#F6FCF9",
                              //                 border: totalPL < 0 ? "1px solid #FBB1B1" : "1px solid #d4d4d4ff",
                              //                 color: totalPL < 0 ? "red" : "green",
                              //                 // backgroundColor: totalCurrentValue < 0 ? "#fff5f5ff" : "#F6FCF9",
                              //                 // border: totalCurrentValue < 0 ? "1px solid #FBB1B1" : "1px solid #d4d4d4ff",
                              //                 // color: totalCurrentValue < 0 ? "red" : "green",
                              //                 minWidth: "60px",
                              //                 textAlign: "center",
                              //               }}
                              //             >
                              //               ₹{formatNumber(totalCurrentValue.toFixed(2))}
                              //             </span>
                              //           );
                              //         }

                              //         else if (col === "Buy Value") {
                              //   const totalBuyValue = filteredHoldings.reduce((sum, h) => {
                              //     // Use BuyQty for stocks, NetQty for CF positions where BuyQty = 0
                              //     // const qty = h.BuyQty || h.NetQty;
                              //     const qty =  h.NetQty || h.BuyQty ;
                              //     const avgRate = h.BuyQty > 0 ? h.BuyAvgRate : h.AvgRate || 0;
                              //     // const buyVal = qty * avgRate * (h.LotSize || 1);
                              //     const buyVal = qty * avgRate * (h.Multiplier || 1);
                              //     return sum + (buyVal || 0);
                              //   }, 0);
                              //   content = `₹${formatNumber(totalBuyValue.toFixed(2))}`;

                              //   const totalPL = filteredHoldings.reduce((sum, h) => sum + calculateMTOM(h), 0);
                              //   content = (
                              //     <span
                              //       style={{
                              //         display: "inline-block",
                              //         padding: "4px 12px",
                              //         borderRadius: "10px",
                              //         // backgroundColor: totalPL < 0 ? "#fff5f5ff" : "#F6FCF9",
                              //         // border: totalPL < 0 ? "1px solid #FBB1B1" : "1px solid #d4d4d4ff",
                              //         border:  "1px solid #b6b6b6ff",
                              //         // color: totalPL < 0 ? "red" : "green",
                              //         minWidth: "60px",
                              //         textAlign: "center",
                              //       }}
                              //     >
                              //       {content}
                              //     </span>
                              //   );
                              // }

                              if (col === "Current Value") {
                                const totalCurrentValue = filteredHoldings.reduce(
                                  (sum, h) => {
                                    const scripCode = h.ScripCode;
                                    const livePrice = parseFloat(
                                      livePriceCache.current[scripCode] ||
                                      h.LTP ||
                                      0,
                                    );
                                    const value = livePrice * (h.NetQty || 0);
                                    const Multiplier = h.Multiplier || 1;
                                    return sum + value * Multiplier;
                                  },
                                  0,
                                );

                                const totalBuyValue = filteredHoldings.reduce(
                                  (sum, h) => {
                                    const qty = h.NetQty;
                                    const avgRate =
                                      h.BuyQty > 0
                                        ? h.BuyAvgRate
                                        : h.AvgRate || 0;
                                    const buyVal =
                                      qty * avgRate * (h.Multiplier || 1);
                                    return sum + (buyVal || 0);
                                  },
                                  0,
                                );

                                const totalPL = filteredHoldings.reduce(
                                  (sum, h) => sum + calculateMTOM(h),
                                  0,
                                );

                                content = (
                                  <div
                                    style={{
                                      textAlign: "center",
                                      width: "120px",
                                      margin: "auto",
                                    }}
                                  >
                                    {/* Current Value Total (Top) */}
                                    <div
                                      style={{
                                        display: "grid",
                                        gridTemplateColumns: "16px 1fr",
                                        alignItems: "center",
                                        justifyItems: "center",
                                        gap: "4px",
                                      }}
                                    >
                                      <span
                                        style={{
                                          fontSize: "0.7em",
                                          opacity: "0.6",
                                          fontWeight: "600",
                                        }}
                                      >
                                        C
                                      </span>
                                      <span
                                        style={{
                                          display: "inline-block",
                                          padding: "4px 12px",
                                          borderRadius: "10px",
                                          backgroundColor:
                                            totalPL < 0 ? "#fff5f5ff" : "#F6FCF9",
                                          border:
                                            totalPL < 0
                                              ? "1px solid #FBB1B1"
                                              : "1px solid #d4d4d4ff",
                                          color: totalPL < 0 ? "red" : "green",
                                          minWidth: "60px",
                                          textAlign: "center",
                                          marginLeft: "10px",
                                        }}
                                      >
                                        ₹
                                        {formatNumber(
                                          totalCurrentValue.toFixed(2),
                                        )}
                                      </span>
                                    </div>

                                    {/* Separator Line */}
                                    <div
                                      style={{
                                        width: "25px",
                                        height: "1px",
                                        background:
                                          "linear-gradient(90deg, transparent, #cbd5e1, transparent)",
                                        margin: "4px auto",
                                        opacity: "0.5",
                                      }}
                                    ></div>

                                    {/* Buy Value Total (Bottom) */}
                                    <div
                                      style={{
                                        display: "grid",
                                        gridTemplateColumns: "16px 1fr",
                                        alignItems: "center",
                                        justifyItems: "center",
                                        gap: "4px",
                                      }}
                                    >
                                      <span
                                        style={{
                                          fontSize: "0.7em",
                                          opacity: "0.6",
                                          fontWeight: "600",
                                        }}
                                      >
                                        B
                                      </span>
                                      <span
                                        style={{
                                          display: "inline-block",
                                          padding: "4px 12px",
                                          borderRadius: "10px",
                                          border: "1px solid #b6b6b6ff",
                                          minWidth: "60px",
                                          textAlign: "center",
                                          marginLeft: "10px",
                                        }}
                                      >
                                        ₹{formatNumber(totalBuyValue.toFixed(2))}
                                      </span>
                                    </div>
                                  </div>
                                );
                              } else if (col === "Buy Value") {
                                content = <></>;
                              } else if (col === "Profit/Loss" || col === "PL") {
                                const totalPL = filteredHoldings.reduce(
                                  (sum, h) => sum + calculateMTOM(h),
                                  0,
                                );
                                content = (
                                  <span
                                    style={{
                                      display: "inline-block",
                                      padding: "4px 12px",
                                      borderRadius: "10px",
                                      backgroundColor:
                                        totalPL < 0 ? "#fff5f5ff" : "#F6FCF9",
                                      border:
                                        totalPL < 0
                                          ? "1px solid #FBB1B1"
                                          : "1px solid #d4d4d4ff",
                                      color: totalPL < 0 ? "red" : "green",
                                      minWidth: "60px",
                                      textAlign: "center",
                                    }}
                                  >
                                    ₹{formatNumber(totalPL.toFixed(2))}
                                  </span>
                                );
                              } else {
                                content = col === "Name" ? "Total" : "";
                              }

                              return (
                                <td
                                  key={col}
                                  className={
                                    col === "Actions"
                                      ? "action-cell"
                                      : undefined
                                  }
                                  style={{
                                    textAlign:
                                      col === "Name"
                                        ? "center"
                                        : ["ProfitPct", "Volume"].includes(col)
                                          ? "center"
                                          : "center",
                                    padding: col === "Name" ? "15px" : undefined,
                                    fontWeight: 600,
                                    display: [
                                      "TodaysLow",
                                      "Buy Quantity",
                                      "Target %",
                                      "Avg Buy Price",
                                    ].includes(col)
                                      ? "none"
                                      : undefined,
                                  }}
                                >
                                  {content}
                                </td>
                              );
                            })}
                          </tr>
                        )}
                      </tbody>
                    </table>
                  </>
                )}
              </div>
              {/* {!isLoading && ( */}
              {!isLoading && holdings.length > 0 && (
                <div className="table__footer_row">
                  <div
                    style={{ display: "flex", alignItems: "center", gap: "10px" }}
                  >
                    <h6>
                      {indexOfFirstItem + 1} to{" "}
                      {Math.min(indexOfLastItem, filteredHoldings.length)} of{" "}
                      {filteredHoldings.length} Items
                    </h6>
                    <TablePageSizeSelect
                      value={pageSize}
                      onChange={handlePageSizeChange}
                    />
                  </div>

                  <nav aria-label="Page navigation example">
                    <ul className="pagination justify-content-center">
                      {/* First Page Button */}
                      <li
                        className={`page-item ${currentPage === 1 ? "disabled" : ""}`}
                      >
                        <a
                          className="page-link"
                          onClick={() => currentPage > 1 && handlePageChange(1)}
                          style={{
                            cursor: currentPage === 1 ? "not-allowed" : "pointer",
                          }}
                          title="First Page"
                        >
                          <MdKeyboardDoubleArrowLeft
                            style={{ fontSize: "18px" }}
                          />
                        </a>
                      </li>

                      {/* Previous Page Button */}
                      <li
                        className={`page-item ${currentPage === 1 ? "disabled" : ""}`}
                      >
                        <a
                          className="page-link"
                          onClick={() =>
                            currentPage > 1 && handlePageChange(currentPage - 1)
                          }
                          style={{
                            cursor: currentPage === 1 ? "not-allowed" : "pointer",
                          }}
                          title="Previous Page"
                        >
                          <IoIosArrowBack />
                        </a>
                      </li>

                      {/* Page Numbers */}
                      {getPageNumbers().map((pageNum) => (
                        <li
                          key={pageNum}
                          className={`page-item ${currentPage === pageNum ? "active" : ""}`}
                        >
                          <a
                            className="page-link"
                            onClick={() => handlePageChange(pageNum)}
                            style={{ cursor: "pointer" }}
                          >
                            {pageNum}
                          </a>
                        </li>
                      ))}

                      {/* Next Page Button */}
                      <li
                        className={`page-item ${currentPage === totalPages ? "disabled" : ""}`}
                      >
                        <a
                          className="page-link"
                          onClick={() =>
                            currentPage < totalPages &&
                            handlePageChange(currentPage + 1)
                          }
                          style={{
                            cursor:
                              currentPage === totalPages
                                ? "not-allowed"
                                : "pointer",
                          }}
                          title="Next Page"
                        >
                          <IoIosArrowForward />
                        </a>
                      </li>

                      {/* Last Page Button */}
                      <li
                        className={`page-item ${currentPage === totalPages ? "disabled" : ""}`}
                      >
                        <a
                          className="page-link"
                          onClick={() =>
                            currentPage < totalPages &&
                            handlePageChange(totalPages)
                          }
                          style={{
                            cursor:
                              currentPage === totalPages
                                ? "not-allowed"
                                : "pointer",
                          }}
                          title="Last Page"
                        >
                          <MdKeyboardDoubleArrowRight
                            style={{ fontSize: "18px" }}
                          />
                        </a>
                      </li>
                    </ul>
                  </nav>
                </div>
              )}
            </div>
          </>
        </div>
      </div>

      {/* Buy Sell Modal - Modern Design */}
      <div
        className="modal fade modal_md modern-trading-modal"
        id="BuySellModal"
        aria-labelledby="BuySellModalLabel"
        aria-hidden="true"
      >
        <div className="modal-dialog modal-dialog-centered modal-dialog-modern">
          <div className="modal-content modal-content-modern">
            {/* Modal Header */}
            <div className="modal-header modal-header-modern">
              <div className="modal-header-content">
                <div className="modal-title-wrapper">
                  <div className={`trading-type-badge ${buySellModalDetails.Type.toLowerCase()}`}>
                    <img
                      src={
                        buySellModalDetails.Type === "Buy"
                          ? ArrowTrendUp
                          : ArrowTrendDown
                      }
                      className="trading-icon"
                      alt="icon"
                    />
                    <span className="trading-type-text">{buySellModalDetails.Type}</span>
                  </div>
                  <h3 className="modal-title-modern">
                    {buySellModalDetails.Symbol}
                  </h3>
                </div>
                <button
                  type="button"
                  className="btn-close-modern"
                  data-bs-dismiss="modal"
                  aria-label={tr("close")}
                >
                  <svg width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                    <line x1="18" y1="6" x2="6" y2="18"></line>
                    <line x1="6" y1="6" x2="18" y2="18"></line>
                  </svg>
                </button>
              </div>
            </div>

            <div className="modal-body modal-body-modern">
              <AnimatePresence mode="wait">
                {/* Screen 1 - Order Entry */}
                {!isPreview && (
                  <motion.div
                    key="screen1"
                    initial={{ y: 30, opacity: 0 }}
                    animate={{ y: 0, opacity: 1 }}
                    exit={{ y: -30, opacity: 0 }}
                    transition={{ duration: 0.3, ease: "easeOut" }}
                  >
                    {/* Stock Info Card */}
                    <div
                      className="sell__content"
                      style={{
                        backgroundColor:
                          buySellModalDetails.Type === "Buy"
                            ? "#F6FCF9"
                            : "#fff7f7",
                        border: `1px solid ${buySellModalDetails.Type === "Buy" ? "#B5E8CF" : "#F5B5B5"}`,
                      }}
                    >
                      <div className="sell__content_inner d-flex justify-content-between align-items-center">
                        <div>
                          <h5 className="mb-0">{buySellModalDetails.Symbol}</h5>
                          <h6 className="mb-0">
                            {buySellModalDetails.FullName}
                          </h6>
                        </div>
                        {/* LivePrice/ Current Price */}
                        <p className="mb-0">
                          {(() => {
                            const order = buySellModalDetails.order;
                            let scripCode;
                            if (order?.Exch === "N") {
                              scripCode = order?.NseCode || order?.ScripCode || order?.scripcode;
                            } else {
                              scripCode = order?.BseCode || order?.ScripCode || order?.scripcode;
                            }
                            const numericScripCode = typeof scripCode === "string" ? parseInt(scripCode) : scripCode;
                            const livePrice = livePriceCache.current[numericScripCode];
                            const pClose = livePCloseCache.current[numericScripCode];
                            const changePercent = liveChangePercentageCache.current[numericScripCode];
                            const currentPrice = livePrice || order?.CurrentPrice || order?.LTP || order?.initialPrice || 0;
                            const previousClose = pClose || order?.CurrentPrice || order?.LTP || order?.initialPrice || 0;
                            const actualChangePercent = changePercent || 0;
                            const isPositive = parseFloat(currentPrice) >= parseFloat(previousClose);

                            return (
                              <>
                                ₹ {formatNumber(currentPrice)} <br />
                                <span
                                  className={
                                    isPositive ? "text-success" : "text-danger"
                                  }
                                >
                                  ({isPositive ? "+" : ""}
                                  {actualChangePercent
                                    ? actualChangePercent.toFixed(2)
                                    : "0.00"}
                                  %)
                                  <i
                                    className={`icon-arrow-${isPositive ? "up" : "down"} ms-1`}
                                    style={{ fontSize: "0.8em" }}
                                  ></i>
                                </span>
                              </>
                            );
                          })()}
                        </p>
                      </div>
                    </div>

                    {/* Order Form */}
                    <div className="row mt-3">
                      <div className="col-md-6">
                        <label className="form-label" style={{ fontSize: '12px', fontWeight: '500', color: '#64748b', marginBottom: '6px' }}>{tr("orderType")}</label>
                        <SelectPicker
                          data={OrderType}
                          searchable={false}
                          container={document.body}
                          className="custom-select-picker"
                          value={orderType}
                          onChange={setOrderType}
                        />
                      </div>

                      <div className="col-md-6">
                        <label className="form-label" style={{ fontSize: '12px', fontWeight: '500', color: '#64748b', marginBottom: '6px' }}>{tr("qty")}</label>
                        <input
                          type="number"
                          className="form-control"
                          value={buySellModalDetails.OrderQty}
                          onChange={(e) =>
                            setBuySellModalDetails((prev) => ({
                              ...prev,
                              OrderQty: Number(e.target.value),
                            }))
                          }
                          style={{
                            width: "100%",
                            height: "44px",
                            borderRadius: "10px",
                            border: "1px solid #d5dfed",
                            padding: "10px 12px",
                            fontSize: "14px",
                            color: "#0f172a",
                            outline: "none",
                            boxShadow: "none"
                          }}
                        />
                      </div>
                    </div>

                    {orderType === "Limit Order" && (
                      <div className="mt-3">
                        <label className="form-label" style={{ fontSize: '12px', fontWeight: '500', color: '#64748b', marginBottom: '6px' }}>{tr("limitPrice")}</label>
                        <input
                          type="number"
                          className="form-control"
                          value={buySellModalDetails.LimitPrice}
                          onChange={(e) =>
                            setBuySellModalDetails((prev) => ({
                              ...prev,
                              LimitPrice: Number(e.target.value),
                            }))
                          }
                          style={{
                            width: "100%",
                            height: "44px",
                            borderRadius: "10px",
                            border: "1px solid #d5dfed",
                            padding: "10px 12px",
                            fontSize: "14px",
                            color: "#0f172a",
                            outline: "none",
                            boxShadow: "none"
                          }}
                        />
                      </div>
                    )}

                    {buySellModalDetails.OrderQty > 0 && (
                      <motion.div
                        className="order-summary mt-4 p-3 border roundedX"
                        initial={{ opacity: 0, height: 0 }}
                        animate={{ opacity: 1, height: "auto" }}
                        exit={{ opacity: 0, height: 0 }}
                        transition={{ duration: 0.4 }}
                        style={{
                          backgroundColor:
                            buySellModalDetails.Type === "Buy"
                              ? "#F6FCF9"
                              : "#fff7f7",
                          border: `1px solid ${buySellModalDetails.Type === "Buy" ? "#B5E8CF" : "#F5B5B5"}`,
                        }}
                      >
                        <h6 className="fw-bold mb-3">{tr("orderSummary")}</h6>
                        <div className="order-row d-flex justify-content-between">
                          <span className="label">{tr("orderType")}</span>
                          <span className="value">{orderType}</span>
                        </div>
                        <div className="order-row d-flex justify-content-between">
                          <span className="label">{tr("quantity")}</span>
                          <span className="value">
                            {buySellModalDetails.OrderQty} {tr("shares")}
                          </span>
                        </div>
                        <div className="order-row d-flex justify-content-between">
                          <span className="label">{tr("pricePerShare")}</span>
                          <span className="value">
                            ₹{formatNumber(getPriceForCalculation())}
                          </span>
                        </div>
                        <div className="order-row d-flex justify-content-between">
                          <span className="label">{tr("subtotal")}</span>
                          <span className="value">
                            ₹
                            {formatNumber(
                              buySellModalDetails.OrderQty *
                              getPriceForCalculation(),
                            )}
                          </span>
                        </div>
                        <div className="order-row d-flex justify-content-between">
                          <span className="label">{tr("estFees")}</span>
                          <span className="value">₹0.00</span>
                        </div>
                        <hr />
                        <div className="order-total d-flex justify-content-between fw-bold">
                          <span>{tr("totalAmount")}</span>
                          <span>
                            ₹
                            {formatNumber(
                              buySellModalDetails.OrderQty *
                              getPriceForCalculation(),
                            )}
                          </span>
                        </div>
                      </motion.div>
                    )}
                  </motion.div>
                )}

                {/* Screen 2 - Order Preview */}
                {isPreview && (
                  <motion.div
                    key="screen2"
                    initial={{ y: 30, opacity: 0 }}
                    animate={{ y: 0, opacity: 1 }}
                    exit={{ y: -30, opacity: 0 }}
                    transition={{ duration: 0.3, ease: "easeOut" }}
                  >
                    <div className="preview-header-modern">
                      <div className={`preview-icon-wrapper ${buySellModalDetails.Type.toLowerCase()}`}>
                        <img
                          src={
                            buySellModalDetails.Type === "Buy"
                              ? ArrowTrendUp
                              : ArrowTrendDown
                          }
                          className="preview-icon"
                          alt="icon"
                        />
                      </div>
                      <h4 className="preview-title">{tr("confirmOrder")}</h4>
                      <p className="preview-subtitle">{tr("pleaseReviewOrderDetails")}</p>
                    </div>

                    <div className="order-summary-preview">
                      <div className="preview-row">
                        <span className="preview-label">{tr("orderType")}</span>
                        <span className="preview-value">{orderType}</span>
                      </div>
                      <div className="preview-row">
                        <span className="preview-label">{tr("quantity")}</span>
                        <span className="preview-value">
                          {buySellModalDetails.OrderQty} {tr("shares")}
                        </span>
                      </div>
                      <div className="preview-row">
                        <span className="preview-label">{tr("pricePerShare")}</span>
                        <span className="preview-value">
                          ₹{formatNumber(getPriceForCalculation())}
                        </span>
                      </div>
                      <div className="preview-row">
                        <span className="preview-label">{tr("subtotal")}</span>
                        <span className="preview-value">
                          ₹{formatNumber(
                            buySellModalDetails.OrderQty *
                            getPriceForCalculation(),
                          )}
                        </span>
                      </div>
                      <div className="preview-row">
                        <span className="preview-label">{tr("estFees")}</span>
                        <span className="preview-value">₹0.00</span>
                      </div>
                      <div className="preview-divider"></div>
                      <div className="preview-total">
                        <span className="preview-total-label">{tr("totalAmount")}</span>
                        <span className="preview-total-value">
                          ₹{formatNumber(
                            buySellModalDetails.OrderQty *
                            getPriceForCalculation() +
                            0.0,
                          )}
                        </span>
                      </div>
                    </div>
                  </motion.div>
                )}
              </AnimatePresence>
            </div>

            {/* Modal Footer */}
            <div className="modal-footer modal-footer-modern">
              {!isPreview ? (
                <div className="footer-actions" style={{ display: "flex", gap: "0.75rem" }}>
                  <button
                    type="button"
                    className="btn-modern btn-secondary-modern"
                    data-bs-dismiss="modal"
                  >
                    {tr("cancel")}
                  </button>
                  <button
                    type="button"
                    className="btn-modern"
                    onClick={handlePreview}
                    disabled={!buySellModalDetails.OrderQty || buySellModalDetails.OrderQty <= 0}
                  >
                    {tr("previewOrder")}
                  </button>
                </div>
              ) : (
                <div className="footer-actions">
                  <button
                    type="button"
                    className="btn-modern btn-secondary-modern"
                    onClick={handleGoBack}
                  >
                    {tr("goBack")}
                  </button>
                  <button
                    type="button"
                    className={`btn-modern btn-success-modern ${buySellModalDetails.Type.toLowerCase()}`}
                    onClick={(e) => {
                      fetchDataPortfolio(
                        buySellModalDetails.order,
                        buySellModalDetails.Type,
                        "Portfolio",
                      );
                      e.target.disabled = true;
                      e.target.textContent = tr("processing");

                      setTimeout(() => {
                        setIsPreview(false);
                        e.target.disabled = false;
                        e.target.textContent = tr("confirmOrder");
                      }, 1000);
                    }}
                    data-bs-dismiss="modal"
                  >
                    {tr("confirmOrder")}
                  </button>
                </div>
              )}
            </div>
          </div>
        </div>
      </div >

      {/* MarketDepth Modal */}
      {/* Market Depth Popover */}
      {showMarketDepth && (
        <MarketDepthPopover
          popoverRef={marketDepthPopoverRef}
          position={marketDepthPosition}
          depthRows={currentMDModal ? marketDepth || [] : []}
          onClose={closeMarketDepthPopover}
          tr={tr}
          formatNumber={formatNumber}
        />
      )}

      <SetTargetModal
        modalId="SetTargetModal"
        stock={targetDetails}
        tr={tr}
        formatNumber={formatNumber}
        apiBaseUrl={pmsApiBaseUrl}
        source="position"
        userId={sharedData?.ID}
        brokerType={sharedData?.broker_type || localStorage.getItem("broker_type") || ""}
        onSuccess={() => {
          setUpdatePositionTrigger((prev) => prev + 1);
        }}
        showSuccessToast={showSuccessToast}
        showErrorToast={showErrorToast}
        showWarningToast={showWarningToast}
      />

      {/* Target Reason Details Modal */}
      <div
        className="modal fade"
        id="TargetReasonDetailsModal"
        aria-labelledby="TargetReasonDetailsModalLabel"
        aria-hidden="true"
      >
        <div className="modal-dialog modal-dialog-centered modal-md">
          <div
            className="modal-content border-0"
            style={{
              backgroundColor: "#FFFFFF",
              borderRadius: "14px",
              boxShadow: "0 18px 40px rgba(0, 0, 0, 0.16)",
              overflow: "hidden",
            }}
          >
            <div
              style={{
                background: "#f5f6ff",
                padding: "1.2rem 1.3rem",
                position: "relative",
              }}
            >
              <button
                type="button"
                className="btn-close position-absolute"
                data-bs-dismiss="modal"
                aria-label={tr("close")}
                style={{ top: "0.9rem", right: "0.9rem", opacity: 0.6, fontSize: "0.7rem" }}
              ></button>
              <h5
                className="mb-1"
                id="TargetReasonDetailsModalLabel"
                style={{
                  color: "#000",
                  fontSize: "1rem",
                  fontWeight: 600,
                  marginRight: "2rem",
                }}
              >
                Target Reason
              </h5>
              <p
                className="mb-0"
                style={{ color: "#757575", fontSize: "0.88rem" }}
              >
                {targetReasonModalData.stockName || "-"}
              </p>
            </div>

            <div style={{ padding: "1.2rem 1.3rem 1.3rem 1.3rem" }}>
              <div style={{ marginBottom: "0.9rem" }}>
                <div
                  style={{
                    fontSize: "0.78rem",
                    fontWeight: 700,
                    color: "#475569",
                    letterSpacing: "0.04em",
                    textTransform: "uppercase",
                  }}
                >
                  Reason
                </div>
                <div
                  style={{
                    marginTop: "0.35rem",
                    fontSize: "0.95rem",
                    color: "#1F2937",
                    fontWeight: 600,
                  }}
                >
                  {targetReasonModalData.reason || "-"}
                </div>
              </div>

              <div>
                <div
                  style={{
                    fontSize: "0.78rem",
                    fontWeight: 700,
                    color: "#475569",
                    letterSpacing: "0.04em",
                    textTransform: "uppercase",
                  }}
                >
                  Details
                </div>
                <div
                  style={{
                    marginTop: "0.4rem",
                    fontSize: "0.92rem",
                    color: "#334155",
                    lineHeight: 1.5,
                    background: "#F8FAFC",
                    border: "1px solid #E2E8F0",
                    borderRadius: "10px",
                    padding: "0.75rem 0.85rem",
                    whiteSpace: "pre-wrap",
                    wordBreak: "break-word",
                  }}
                >
                  {targetReasonModalData.details ||
                    "No additional details provided."}
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>

      {/* CDSL Authorization Modal */}

      <button
        id="openCDSLModalBtn"
        type="button"
        class="d-none"
        data-bs-toggle="modal"
        data-bs-target="#CDSLAuthorizationModal"
      ></button>

      <div
        className="modal fade"
        id="CDSLAuthorizationModal"
        aria-labelledby="CDSLAuthorizationModalLabel"
        aria-hidden="true"
        data-bs-backdrop="static"
        data-bs-keyboard="false"
      >
        <div className="modal-dialog modal-dialog-centered modal-md">
          <div
            className="modal-content shadow-lg rounded-4"
            style={{
              backgroundColor: "#ffffffff",
              fontFamily: "'Segoe UI', Tahoma, Geneva, Verdana, sans-serif",
              border: "2px solid #ff6b35",
            }}
          >
            <div
              className="modal-header pb-0"
              style={{ borderBottom: "2px solid #ff6b35" }}
            >
              <h5
                className="modal-title"
                id="CDSLAuthorizationModalLabel"
                style={{
                  color: "#212529",
                  fontWeight: "700",
                  fontSize: "1.5rem",
                  paddingBottom: "12px",
                }}
              >
                Authorize CDSL Access
              </h5>
              <button
                type="button"
                className="btn-close"
                data-bs-dismiss="modal"
                aria-label={tr("close")}
                style={{ filter: "brightness(0.5)" }}
              />
            </div>

            <div
              className="modal-body py-4 text-center"
              style={{ color: "#2e3031ff" }}
            >
              <div className="mb-3">
                <svg
                  xmlns="http://www.w3.org/2000/svg"
                  width="64"
                  height="64"
                  fill="#ff6b35"
                  className="bi bi-shield-lock"
                  viewBox="0 0 16 16"
                >
                  <path d="M5.338 1.59a61.44 61.44 0 0 0-2.837.856.481.481 0 0 0-.328.39c-.554 4.157.726 7.19 2.253 9.188a10.725 10.725 0 0 0 2.287 2.233c.346.244.652.42.893.533.12.057.218.095.293.118a.55.55 0 0 0 .101.025.615.615 0 0 0 .1-.025c.076-.023.174-.061.294-.118.24-.113.547-.29.893-.533a10.726 10.726 0 0 0 2.287-2.233c1.527-1.997 2.807-5.031 2.253-9.188a.48.48 0 0 0-.328-.39c-.651-.213-1.75-.56-2.837-.855C9.552 1.29 8.531 1.067 8 1.067c-.53 0-1.552.223-2.662.524zM5.072.56C6.157.265 7.31 0 8 0s1.843.265 2.928.56c1.11.3 2.229.655 2.887.87a1.54 1.54 0 0 1 1.044 1.262c.596 4.477-.787 7.795-2.465 9.99a11.775 11.775 0 0 1-2.517 2.453 7.159 7.159 0 0 1-1.048.625c-.28.132-.581.24-.829.24s-.548-.108-.829-.24a7.158 7.158 0 0 1-1.048-.625 11.777 11.777 0 0 1-2.517-2.453C1.928 10.487.545 7.169 1.141 2.692A1.54 1.54 0 0 1 2.185 1.43 62.456 62.456 0 0 1 5.072.56z" />
                  <path d="M9.5 6.5a1.5 1.5 0 0 1-1 1.415l.385 1.99a.5.5 0 0 1-.491.595h-.788a.5.5 0 0 1-.49-.595l.384-1.99a1.5 1.5 0 1 1 2-1.415z" />
                </svg>
              </div>

              <h3
                style={{
                  fontWeight: "600",
                  marginBottom: "1rem",
                  color: "#212529",
                }}
              >
                CDSL Sell Authorization Required
              </h3>

              <p
                style={{
                  fontSize: "1.05rem",
                  marginBottom: "2rem",
                  lineHeight: "1.6",
                }}
              >
                Authorize CDSL access to enable stock selling functionality.
                Without permission this feature will not be available.
              </p>

              <div className="d-flex justify-content-center gap-3">
                <button
                  type="button"
                  className="btn btn-outline-secondary px-4 py-2 fw-semibold"
                  data-bs-dismiss="modal"
                  style={{
                    borderRadius: "30px",
                    fontSize: "1rem",
                    letterSpacing: "0.03em",
                  }}
                >
                  Cancel
                </button>

                <form
                  name="CDSLAuthForm"
                  method="post"
                  action="https://dev-openapi.5paisa.com/WebVendorLogin/EDISAuthorization/Authorization"
                  target="_blank"
                  style={{ margin: 0 }}
                >
                  <input
                    type="hidden"
                    name="AccessToken"
                    value={sharedData.accessToken}
                  />
                  <input
                    type="hidden"
                    name="ClientCode"
                    value={sharedData.ClientCode}
                  />
                  <input
                    type="hidden"
                    name="VendorKey"
                    value={sharedData.api_key}
                  />
                  <input
                    type="hidden"
                    name="ResponseURL"
                    value={`https://beta.infomanav.in/zerodhaReact/authorization/${sharedData.ID}`}
                  />

                  <button
                    type="submit"
                    className="btn px-4 py-2 fw-semibold"
                    style={{
                      borderRadius: "30px",
                      fontSize: "1rem",
                      letterSpacing: "0.03em",
                      backgroundColor: "#ff6b35",
                      color: "white",
                      border: "none",
                    }}
                    data-bs-dismiss="modal"
                  >
                    Authorize
                  </button>
                </form>
              </div>
            </div>
          </div>
        </div>
      </div>

      <button
        id="openGrowwTPINModalBtn"
        type="button"
        className="d-none"
        data-bs-toggle="modal"
        data-bs-target="#GrowwTPINModal"
      ></button>

      <div
        className="modal fade"
        id="GrowwTPINModal"
        aria-labelledby="GrowwTPINModalLabel"
        aria-hidden="true"
        data-bs-backdrop="static"
        data-bs-keyboard="false"
      >
        <div className="modal-dialog modal-dialog-centered modal-md">
          <div
            className="modal-content shadow-lg rounded-4"
            style={{
              backgroundColor: "#ffffffff",
              fontFamily: "'Segoe UI', Tahoma, Geneva, Verdana, sans-serif",
              border: "2px solid #ff6b35",
            }}
          >
            <div
              className="modal-header pb-0"
              style={{ borderBottom: "2px solid #ff6b35" }}
            >
              <h5
                className="modal-title"
                id="GrowwTPINModalLabel"
                style={{
                  color: "#212529",
                  fontWeight: "700",
                  fontSize: "1.3rem",
                  paddingBottom: "12px",
                }}
              >
                Groww TPIN Verification Required
              </h5>
              <button
                type="button"
                className="btn-close"
                data-bs-dismiss="modal"
                aria-label={tr("close")}
                style={{ filter: "brightness(0.5)" }}
              />
            </div>

            <div
              className="modal-body py-4 text-center"
              style={{ color: "#2e3031ff" }}
            >
              <h3
                style={{
                  fontWeight: "600",
                  marginBottom: "1rem",
                  color: "#212529",
                }}
              >
                Sell Order Could Not Be Placed
              </h3>
              <p
                style={{
                  fontSize: "1.02rem",
                  marginBottom: "0.8rem",
                  lineHeight: "1.6",
                }}
              >
                {growwTpinErrorText ||
                  "Verify your holdings first via TPIN from Groww Dashboard."}
              </p>
              <p
                style={{
                  fontSize: "0.95rem",
                  marginBottom: "2rem",
                  color: "#495057",
                }}
              >
                Complete TPIN verification, then try the sell order again.
              </p>

              <div className="d-flex justify-content-center gap-3">
                <button
                  type="button"
                  className="btn btn-outline-secondary px-4 py-2 fw-semibold"
                  data-bs-dismiss="modal"
                  style={{
                    borderRadius: "30px",
                    fontSize: "1rem",
                    letterSpacing: "0.03em",
                  }}
                >
                  {tr("cancel")}
                </button>
                <a
                  href="https://groww.in/cdslauth"
                  target="_blank"
                  rel="noopener noreferrer"
                  className="btn px-4 py-2 fw-semibold"
                  style={{
                    borderRadius: "30px",
                    fontSize: "1rem",
                    letterSpacing: "0.03em",
                    backgroundColor: "#ff6b35",
                    color: "white",
                    border: "none",
                  }}
                >
                  Verify TPIN
                </a>
              </div>
            </div>
          </div>
        </div>
      </div>

      <StockCandleModal
        isOpen={!!selectedStock}
        onClose={() => setSelectedStock(null)}
        Exch={selectedStock?.Exch}
        ExchType={selectedStock?.ExchType}
        ExchSeg={selectedStock?.ExchSeg || selectedStock?.exchange}
        ScripCode={selectedStock?.ScripCode}
        symbolToken={
          selectedStock?.token ||
          selectedStock?.symboltoken ||
          selectedStock?.Token ||
          selectedStock?.scripcode ||
          selectedStock?.ScripCode
        }
        Name={selectedStock?.FullName || selectedStock?.name}
        Symbol={selectedStock?.Symbol || selectedStock?.tradingsymbol}
      />



    </>
  );
}

export default Position;
